import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiActivity, FiRefreshCw, FiEye, FiRadio } from 'react-icons/fi';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../services/api';
import Modal from '../components/Modal';
import FourShineMark from '../components/FourShineMark';
import { parseServerDate, formatDateTimeNL, formatTimeNL } from '../utils/dateFormat';
import { signupSourceLabel } from '../utils/marketingSignup';
import { CACHE_KEYS, TTL, peekCache, setCache, warmCache } from '../services/pageDataCache';
import './Attributie.css';

function MarketingSignupDetail({ signup }) {
  if (!signup) return null;
  return (
    <>
      <h4>Nieuwsbrief / pop-up</h4>
      <p className="hint marketing-signup-detail">
        Ingeschreven via <strong>{signupSourceLabel(signup.signup_source)}</strong>
        {signup.subscriber_name ? <> · {signup.subscriber_name}</> : null}
        {signup.email ? <> · {signup.email}</> : null}
        {signup.variant ? <> · {signup.variant}</> : null}
      </p>
    </>
  );
}

const HERKOMST_COLORS = ['#3FDFCF', '#ff6b6b', '#2fb8a8', '#6c5ce7', '#fdcb6e', '#00b894', '#e17055', '#a29bfe'];

/** Vandaag in lokale tijd als YYYY-MM-DD (standaard voor date range). */
function getTodayLocal() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** Pad + query voor configurator-link; volledige URL in title (ellipsis via CSS op desktop). */
function configuratorPageLinkParts(rowLink) {
  if (!rowLink) return { href: null, text: '—', title: '' };
  try {
    const u = new URL(rowLink);
    return { href: rowLink, text: (u.pathname || '/') + (u.search || ''), title: rowLink };
  } catch {
    return { href: rowLink, text: rowLink, title: rowLink };
  }
}

function formatSecondsAgo(dateStr) {
  const date = parseServerDate(dateStr);
  if (!date || isNaN(date.getTime())) return '—';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 0) return 'zojuist';
  if (sec < 60) return `${sec} s geleden`;
  return `${Math.floor(sec / 60)} min geleden`;
}

const COUNTRY_NAMES = { NL: 'Nederland', BE: 'België', DE: 'Duitsland', FR: 'Frankrijk', GB: 'Verenigd Koninkrijk', UK: 'Verenigd Koninkrijk', US: 'Verenigde Staten', ES: 'Spanje', IT: 'Italië', PL: 'Polen', TR: 'Turkije', IN: 'India', CN: 'China', JP: 'Japan' };

function formatLocatie(v) {
  if (!v || typeof v !== 'object') return '—';
  const country = v.country != null ? (COUNTRY_NAMES[String(v.country).toUpperCase()] || v.country) : null;
  const city = v.city != null ? String(v.city) : null;
  if (city && country) return `${city}, ${country}`;
  if (country) return country;
  if (city) return city;
  return '—';
}

function formatHerkomst(v) {
  if (v.utm_source) {
    const m = v.utm_medium ? ` / ${v.utm_medium}` : '';
    return `${v.utm_source}${m}`;
  }
  if (v.referrer) {
    try {
      const u = new URL(v.referrer);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return v.referrer.length > 40 ? v.referrer.slice(0, 40) + '…' : v.referrer;
    }
  }
  return 'direct';
}

function isMogelijkBot(ua) {
  if (!ua || typeof ua !== 'string') return false;
  return /bot|crawler|spider|headless|googlebot|facebookexternalhit|curl|wget|python-|java\//i.test(ua);
}

function formatBronMedium(source, medium) {
  if (source == null || source === '') return medium ? String(medium) : '—';
  return medium ? `${source} / ${medium}` : String(source);
}

/**
 * Rijen met betaalde Google of Meta (fb/ig/…): uitklappen voor campagne-detail (ad_platform_campaign_metrics).
 * Zelfde logica als server resolvedAdMetricsKey voor organic/paid.
 */
function getCampaignBreakdownPlatform(bron) {
  const raw = String(bron || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const ix = lower.indexOf('/');
  const base = ix === -1 ? lower : lower.slice(0, ix).trim();
  const medium = ix === -1 ? '' : lower.slice(ix + 1).trim().split('/').map((x) => x.trim())[0];
  const googleOrganic = new Set(['organic', 'referral', '(none)', 'none', 'natural', 'social', 'email']);
  const metaOrganic = new Set(['organic', 'referral', 'social', '(none)', 'none', 'email']);
  const metaBases = new Set(['meta', 'facebook', 'fb', 'instagram', 'ig']);

  if (base === 'google') {
    if (ix === -1) return 'google';
    if (googleOrganic.has(medium)) return null;
    return 'google';
  }
  if (metaBases.has(base)) {
    if (ix === -1) return 'meta';
    if (metaOrganic.has(medium)) return null;
    return 'meta';
  }
  return null;
}

/** Weergavenaam voor tooltips in campagnedetail. */
function getAdsPlatformLabel(platform) {
  if (platform === 'meta') return 'Meta Ads';
  if (platform === 'google') return 'Google Ads';
  return 'Ads-platform';
}

function formatSeconden(sec) {
  if (sec == null || typeof sec !== 'number' || isNaN(sec)) return '—';
  if (sec >= 60) return `${Math.round(sec / 60)} min`;
  return `${Math.round(sec)} s`;
}

/** Tijd in periode als HH:MM:SS (zoals andere live-view apps). */
function formatDurationHMS(sec) {
  if (sec == null || typeof sec !== 'number' || isNaN(sec) || sec < 0) return '00:00:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/** Landcode (2 letters) naar vlag-emoji. */
function countryToFlag(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return null;
  const c = code.toUpperCase();
  return [...c].map((char) => String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0))).join('');
}

/** Referrer-URL kort tonen (hostname of afgekapt). */
function formatReferrerShort(referrer) {
  if (!referrer || typeof referrer !== 'string') return '—';
  const s = referrer.trim();
  if (!s) return '—';
  try {
    const u = new URL(s);
    const host = (u.hostname || '').replace(/^www\./, '');
    return host.length > 28 ? host.slice(0, 25) + '…' : host;
  } catch {
    return s.length > 28 ? s.slice(0, 25) + '…' : s;
  }
}

/** Live-rij: checkout (donkerder 4SHINE-groen) wint van winkelmand (lichter). */
function liveVisitorFunnelClass(v) {
  if (!v) return '';
  if (v.visited_checkout) return 'live-row-checkout';
  if (v.visited_cart) return 'live-row-cart';
  return '';
}

/** Bron-shop uit tracking (default Woo). */
function liveShopPlatform(v) {
  return v && String(v.shop_platform).toLowerCase() === 'shopify' ? 'shopify' : 'woo';
}

const MOBILE_BREAKPOINT = 768;

export default function Attributie() {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [overview, setOverview] = useState(null);
  const [bezoeken, setBezoeken] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateStart, setDateStart] = useState(() => getTodayLocal());
  const [dateEnd, setDateEnd] = useState(() => getTodayLocal());
  const [selectedPreset, setSelectedPreset] = useState('');
  const [journeyOrderId, setJourneyOrderId] = useState('');
  const [journey, setJourney] = useState(null);
  const [liveMinutes, setLiveMinutes] = useState(5);
  const [liveVisitors, setLiveVisitors] = useState(() => {
    const cached = peekCache(CACHE_KEYS.live(5), TTL.LIVE_TTL * 3);
    return Array.isArray(cached?.live) ? cached.live : [];
  });
  const [expandedVisitorId, setExpandedVisitorId] = useState(null);
  const [visitorBezoekenCache, setVisitorBezoekenCache] = useState({});
  const [expandedBezoekId, setExpandedBezoekId] = useState(null);
  const [recenteVisitorModalId, setRecenteVisitorModalId] = useState(null);
  const [ordersOfDay, setOrdersOfDay] = useState({ date: null, regels: [] });
  const [orderDetailModalData, setOrderDetailModalData] = useState(null);
  const [orderJourney, setOrderJourney] = useState(null);
  const [herkomstPeriode, setHerkomstPeriode] = useState([]);
  const [visitorCustomers, setVisitorCustomers] = useState({}); // visitor_id -> klant_naam of klant_email
  const [visitorMarketing, setVisitorMarketing] = useState({}); // visitor_id -> pop-up/footer inschrijving
  const [orderwaardePeriode, setOrderwaardePeriode] = useState(null); // orderwaarde voor geselecteerde periode
  const [visitsPerHour, setVisitsPerHour] = useState([]); // bezoeken per uur voor gekozen dag
  const [gemiddeldeTijdOnlineSeconden, setGemiddeldeTijdOnlineSeconden] = useState(null); // gem. tijd eerste–laatste bezoek per bezoeker (periode)
  const [diagnose, setDiagnose] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [attributieTabelData, setAttributieTabelData] = useState(null); // { rows, totals } van GET attributie-tabel
  const [adMetricsModalOpen, setAdMetricsModalOpen] = useState(false);
  const [adMetricsForm, setAdMetricsForm] = useState({ bron: '', start_date: '', end_date: '', spend: '', clicks: '', impressions: '', budget: '' });
  const [adApiConfig, setAdApiConfig] = useState({ google: { configured: false }, meta: { configured: false }, pinterest: { configured: false } });
  const [adApiConfigModalOpen, setAdApiConfigModalOpen] = useState(false);
  const [adApiConfigForm, setAdApiConfigForm] = useState({
    google: { client_id: '', client_secret: '', developer_token: '', refresh_token: '', customer_id: '', login_customer_id: '' },
    meta: { access_token: '', ad_account_id: '' },
    pinterest: { access_token: '', ad_account_id: '' }
  });
  const [pinterestCapiConfig, setPinterestCapiConfig] = useState({ configured: false });
  const [pinterestCapiForm, setPinterestCapiForm] = useState({ access_token: '', ad_account_id: '' });
  const [syncAdApiLoading, setSyncAdApiLoading] = useState(false);
  const [syncAdApiResult, setSyncAdApiResult] = useState(null);
  /** Uitklapbare rij in Attributie-tabel (campagnes o.a. Google). */
  const [expandedAttributieBron, setExpandedAttributieBron] = useState(null);
  /** `${platform}|${start}|${end}` → { loading, loaded, campaigns, error } */
  const [campaignBreakdownCache, setCampaignBreakdownCache] = useState({});
  /** shop|bron|start|end → { loading, orders, error } — shoporders metzelfde bronlabel als attributie-tabel */
  const [shopOrdersForBronCache, setShopOrdersForBronCache] = useState({});
  /** metaJourney|start|end → orders met Meta-touch in klantreis (proxy voor wat FB kan meten) */
  const [metaJourneyOrdersCache, setMetaJourneyOrdersCache] = useState({});
  const [trackingEventsSummary, setTrackingEventsSummary] = useState({}); // { event_name: count }
  const [trackingEventsList, setTrackingEventsList] = useState([]); // volledige lijst voor overzicht per dag
  const [bronMethode, setBronMethode] = useState('first_touch'); // first_touch | last_touch – voor bron-verrijking bij sync

  const fetchBronMethode = useCallback(async () => {
    try {
      const res = await api.get('/parameters/attributie_bron_methode');
      const v = res.data?.waarde;
      if (v && (v === 'first_touch' || v === 'last_touch')) setBronMethode(v);
    } catch {
      // 404 of andere fout: standaard first_touch
    }
  }, []);

  const saveBronMethode = useCallback(async (value) => {
    setBronMethode(value);
    try {
      await api.post('/parameters', {
        parameter_naam: 'attributie_bron_methode',
        waarde: value,
        beschrijving: 'Bron-attributie bij verrijking: first_touch of last_touch (orders met Direct/lege bron krijgen bron uit bezoeken, binnen 2 maanden)'
      });
    } catch (e) {
      console.error('Bron-methode opslaan mislukt:', e);
    }
  }, []);

  useEffect(() => {
    fetchBronMethode();
  }, [fetchBronMethode]);

  const fetchTrackingEvents = useCallback(async () => {
    if (!dateStart || !dateEnd) return;
    try {
      const res = await api.get(`/attributie/tracking-events?start_date=${dateStart}&end_date=${dateEnd}`);
      const events = res.data?.events || [];
      const byName = {};
      events.forEach((e) => {
        const n = e.event_name || 'unknown';
        byName[n] = (byName[n] || 0) + 1;
      });
      setTrackingEventsSummary(byName);
      // Alleen echte configurator-start (productpagina); geen categorie-icoon-klikken
      const roomVisualizerEvents = events.filter((e) => e.event_name === 'room_visualizer_open');
      setTrackingEventsList(roomVisualizerEvents);
    } catch {
      setTrackingEventsSummary({});
      setTrackingEventsList([]);
    }
  }, [dateStart, dateEnd]);

  const fetchDiagnose = useCallback(async () => {
    setDiagnose(null);
    setDiagnoseLoading(true);
    try {
      const res = await api.get(`/attributie/diagnose?minutes=${liveMinutes}`);
      setDiagnose(res.data);
    } catch (e) {
      setDiagnose({ error: e.response?.data?.error || e.message });
    } finally {
      setDiagnoseLoading(false);
    }
  }, [liveMinutes]);

  const fetchOrdersOfDay = useCallback(() => {
    if (!dateStart || !dateEnd) return;
    if (dateStart === dateEnd) {
      const after = `${dateStart}T00:00:00.000Z`;
      const before = `${dateStart}T23:59:59.999Z`;
      api.post('/woocommerce/import/sales', { after, before })
        .catch(() => {})
        .finally(() => {
          api.get(`/woocommerce/sales/day/${dateStart}`)
            .then(res => setOrdersOfDay({ date: res.data.date, start_date: dateStart, end_date: dateEnd, regels: res.data.regels || [] }))
            .catch(() => setOrdersOfDay({ date: null, start_date: dateStart, end_date: dateEnd, regels: [] }));
        });
    } else {
      api.get(`/woocommerce/sales/range?start_date=${dateStart}&end_date=${dateEnd}`)
        .then(res => setOrdersOfDay({ date: null, start_date: res.data.start_date, end_date: res.data.end_date, regels: res.data.regels || [] }))
        .catch(() => setOrdersOfDay({ date: null, start_date: dateStart, end_date: dateEnd, regels: [] }));
    }
  }, [dateStart, dateEnd]);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await api.get(`/attributie/overview?start_date=${dateStart}&end_date=${dateEnd}`);
      const data = res && res.data;
      if (data && typeof data === 'object') {
        setOverview({
          totaal_bezoeken: data.totaal_bezoeken ?? 0,
          unieke_bezoekers: data.unieke_bezoekers ?? 0,
          per_bron: Array.isArray(data.per_bron) ? data.per_bron : []
        });
      } else {
        setOverview({ totaal_bezoeken: 0, unieke_bezoekers: 0, per_bron: [] });
      }
    } catch (e) {
      console.error(e);
      setOverview({ totaal_bezoeken: 0, unieke_bezoekers: 0, per_bron: [] });
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd]);

  const fetchBezoeken = useCallback(async () => {
    try {
      const res = await api.get(`/attributie/bezoeken?start_date=${dateStart}&end_date=${dateEnd}&limit=50`);
      setBezoeken(res.data.bezoeken || []);
    } catch {
      setBezoeken([]);
    }
  }, [dateStart, dateEnd]);

  const fetchOrderwaardePeriode = useCallback(async () => {
    if (!dateStart || !dateEnd) return;
    try {
      const res = await api.get(`/woocommerce/sales/total?start_date=${dateStart}&end_date=${dateEnd}`);
      const totaal = res?.data?.totaal;
      setOrderwaardePeriode(typeof totaal === 'number' ? totaal : (parseFloat(totaal) || 0));
    } catch {
      setOrderwaardePeriode(0);
    }
  }, [dateStart, dateEnd]);

  const fetchHerkomstPeriode = useCallback(async () => {
    if (!dateStart || !dateEnd) return;
    try {
      const res = await api.get(`/dashboard/herkomst?start_date=${dateStart}&end_date=${dateEnd}`);
      setHerkomstPeriode(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHerkomstPeriode([]);
    }
  }, [dateStart, dateEnd]);

  const setDatePresetAttributie = (preset) => {
    const today = new Date();
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    let start = new Date(today);

    switch (preset) {
      case 'currentweek': {
        const day = today.getDay();
        const toMonday = day === 0 ? 6 : day - 1;
        start.setDate(today.getDate() - toMonday);
        break;
      }
      case 'currentmonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'currentquarter': {
        const currentMonth = today.getMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        start = new Date(today.getFullYear(), quarterStartMonth, 1);
        break;
      }
      case 'halfyear':
        start.setMonth(today.getMonth() - 6);
        break;
      case 'year':
        start.setFullYear(today.getFullYear() - 1);
        break;
      case 'yeartodate':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    start.setHours(0, 0, 0, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    setDateStart(startStr);
    setDateEnd(endStr);
    setSelectedPreset(preset);
  };

  const fetchVisitsByHour = useCallback(async (date) => {
    if (!date) return;
    try {
      const res = await api.get(`/attributie/visits-by-hour?date=${date}`);
      setVisitsPerHour(Array.isArray(res.data) ? res.data : []);
    } catch {
      setVisitsPerHour([]);
    }
  }, []);

  const fetchGemiddeldeTijdOnline = useCallback(async () => {
    if (!dateStart || !dateEnd) return;
    try {
      const res = await api.get(`/attributie/gemiddelde-tijd-online?start_date=${dateStart}&end_date=${dateEnd}`);
      const sec = res?.data?.seconden;
      if (sec === null || sec === undefined || sec === '') {
        setGemiddeldeTijdOnlineSeconden(null);
      } else {
        const n = typeof sec === 'number' ? sec : parseInt(sec, 10);
        setGemiddeldeTijdOnlineSeconden(Number.isNaN(n) ? null : n);
      }
    } catch {
      setGemiddeldeTijdOnlineSeconden(null);
    }
  }, [dateStart, dateEnd]);

  const fetchAttributieTabel = useCallback(async () => {
    if (!dateStart || !dateEnd) return;
    try {
      const res = await api.get(`/attributie/attributie-tabel?start_date=${dateStart}&end_date=${dateEnd}`);
      setAttributieTabelData(res.data || null);
    } catch {
      setAttributieTabelData(null);
    }
  }, [dateStart, dateEnd]);

  const fetchAdApiConfig = useCallback(async () => {
    try {
      const res = await api.get('/attributie/ad-api-config');
      const data = res.data || { google: { configured: false }, meta: { configured: false }, pinterest: { configured: false } };
      setAdApiConfig(data);
      // Vul formulier met opgeslagen niet-geheime velden (geheimen worden nooit teruggestuurd)
      setAdApiConfigForm(prev => ({
        ...prev,
        google: {
          ...prev.google,
          client_id: data.google?.client_id ?? prev.google.client_id,
          customer_id: data.google?.customer_id ?? prev.google.customer_id,
          login_customer_id: data.google?.login_customer_id ?? prev.google.login_customer_id
        },
        meta: {
          ...prev.meta,
          ad_account_id: data.meta?.ad_account_id ?? prev.meta.ad_account_id
        },
        pinterest: {
          ...prev.pinterest,
          ad_account_id: data.pinterest?.ad_account_id ?? prev.pinterest.ad_account_id
        }
      }));
    } catch {
      setAdApiConfig({ google: { configured: false }, meta: { configured: false }, pinterest: { configured: false } });
    }
  }, []);

  const fetchPinterestCapiConfig = useCallback(async () => {
    try {
      const res = await api.get('/attributie/pinterest-capi-config');
      setPinterestCapiConfig(res.data || { configured: false });
      setPinterestCapiForm(prev => ({
        ...prev,
        ad_account_id: res.data?.ad_account_id ?? prev.ad_account_id
      }));
    } catch {
      setPinterestCapiConfig({ configured: false });
    }
  }, []);

  const fetchLive = useCallback(async () => {
    const cacheKey = CACHE_KEYS.live(liveMinutes);
    try {
      const data = await warmCache(
        cacheKey,
        () => api.get(`/attributie/live?minutes=${liveMinutes}`).then((r) => r.data),
        TTL.LIVE_TTL
      );
      const raw = data?.live;
      setLiveVisitors(Array.isArray(raw) ? raw : []);
      setCache(cacheKey, data);
    } catch {
      /* behoud vorige lijst bij tijdelijke fout */
    }
  }, [liveMinutes]);

  const resetVisitorForTest = useCallback(
    async (visitorId) => {
      if (!visitorId) return;
      // eslint-disable-next-line no-alert -- bewuste bevestiging test-reset
      if (
        !window.confirm(
          'Pop-up test reset voor deze bezoeker?\n\nInschrijving + pop-up cooldown worden gewist. Bezoeken en orders blijven staan.'
        )
      ) {
        return;
      }
      try {
        await api.post('/attributie/reset-test-visitor', { visitor_id: visitorId });
        setVisitorMarketing((prev) => {
          const next = { ...prev };
          delete next[visitorId];
          return next;
        });
        fetchLive();
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert(e.response?.data?.error || e.message || 'Reset mislukt');
      }
    },
    [fetchLive]
  );

  /** Ontkoppel browser ↔ klant-e-mail (orders blijven; voor verkeerde staff-koppeling). */
  const unlinkVisitorEmail = useCallback(
    async (visitorId, emailHint) => {
      if (!visitorId) return;
      const email =
        emailHint && String(emailHint).includes('@') ? String(emailHint).trim() : undefined;
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          `Browser ontkoppelen van ${email || 'gekoppelde order-e-mail(s)'}?\n\nOrders en inschrijvingen blijven bestaan. Wis daarna op 4shine localStorage inkoop_vid.`
        )
      ) {
        return;
      }
      try {
        const res = await api.post('/marketing/popup/unlink-visitor', {
          visitor_id: visitorId,
          email: email || undefined,
        });
        setVisitorMarketing((prev) => {
          const next = { ...prev };
          delete next[visitorId];
          return next;
        });
        setVisitorCustomers((prev) => {
          const next = { ...prev };
          delete next[visitorId];
          return next;
        });
        fetchLive();
        // eslint-disable-next-line no-alert
        window.alert(
          `Ontkoppeld (${res.data?.removed?.order_visitor_links ?? 0} order-links). ${res.data?.hint || ''}`
        );
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert(e.response?.data?.error || e.message || 'Ontkoppelen mislukt');
      }
    },
    [fetchLive]
  );

  const fetchBezoekenForVisitor = useCallback(async (visitorId) => {
    if (visitorId == null || visitorId === '__unknown__') return;
    try {
      const res = await api.get(`/attributie/bezoeken?visitor_id=${encodeURIComponent(visitorId)}&limit=200`);
      const list = res.data?.bezoeken || [];
      setVisitorBezoekenCache(prev => ({ ...prev, [visitorId]: list }));
    } catch {
      setVisitorBezoekenCache(prev => ({
        ...prev,
        [visitorId]: prev[visitorId] !== undefined ? prev[visitorId] : []
      }));
    }
  }, []);

  useEffect(() => {
    fetchAdApiConfig();
    fetchPinterestCapiConfig();
  }, [fetchAdApiConfig, fetchPinterestCapiConfig]);

  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchOverview();
      fetchBezoeken();
      fetchOrderwaardePeriode();
      fetchHerkomstPeriode();
      fetchVisitsByHour(dateEnd);
      fetchGemiddeldeTijdOnline();
      fetchAttributieTabel();
      fetchTrackingEvents();
    }
  }, [dateStart, dateEnd, fetchOverview, fetchBezoeken, fetchOrderwaardePeriode, fetchHerkomstPeriode, fetchVisitsByHour, fetchGemiddeldeTijdOnline, fetchAttributieTabel, fetchTrackingEvents]);

  useEffect(() => {
    if (!dateStart || !dateEnd) return;
    const interval = setInterval(fetchBezoeken, 30000);
    return () => clearInterval(interval);
  }, [dateStart, dateEnd, fetchBezoeken]);

  useEffect(() => {
    if (!dateStart || !dateEnd) return;
    const interval = setInterval(fetchOrderwaardePeriode, 60000);
    return () => clearInterval(interval);
  }, [dateStart, dateEnd, fetchOrderwaardePeriode]);

  useEffect(() => {
    fetchOrdersOfDay();
  }, [fetchOrdersOfDay]);

  useEffect(() => {
    const interval = setInterval(fetchOrdersOfDay, 60000);
    return () => clearInterval(interval);
  }, [fetchOrdersOfDay]);

  useEffect(() => {
    const live = Array.isArray(liveVisitors) ? liveVisitors : [];
    const bezoek = Array.isArray(bezoeken) ? bezoeken : [];
    const ids = [...new Set([...live.map(v => v?.visitor_id), ...bezoek.map(b => b && b.visitor_id)].filter(Boolean))];
    const fromLive = {};
    live.forEach((v) => {
      if (v?.visitor_id && v.marketing_signup) fromLive[v.visitor_id] = v.marketing_signup;
    });
    if (Object.keys(fromLive).length) {
      setVisitorMarketing((prev) => ({ ...prev, ...fromLive }));
    }
    if (ids.length === 0) return;
    api.get(`/attributie/visitor-customers?visitor_ids=${ids.join(',')}`)
      .then(res => setVisitorCustomers(res.data || {}))
      .catch(() => setVisitorCustomers({}));
    api.get(`/attributie/visitor-marketing?visitor_ids=${ids.join(',')}`)
      .then((res) => setVisitorMarketing((prev) => ({ ...prev, ...(res.data || {}) })))
      .catch(() => {});
  }, [liveVisitors, bezoeken]);

  /** Naast “live bezoekers”: gemiddelde sessieduur van actieve bezoekers (nu − eerste hit); anders periode-gemiddelde uit API. */
  const herkomstGemiddeldeSeconden = useMemo(() => {
    const live = Array.isArray(liveVisitors) ? liveVisitors : [];
    if (live.length > 0) {
      const sum = live.reduce((s, v) => s + (Number(v.duration_sec) || 0), 0);
      return Math.round(sum / live.length);
    }
    return gemiddeldeTijdOnlineSeconden;
  }, [liveVisitors, gemiddeldeTijdOnlineSeconden]);

  const fetchJourney = async () => {
    if (!journeyOrderId.trim()) return;
    setJourney(null);
    try {
      const res = await api.get(`/attributie/journey/${journeyOrderId.trim()}`);
      setJourney(res.data);
    } catch {
      setJourney({ order_id: journeyOrderId, bezoeken: [], google_campagne: null, attributie_bron_methode: 'first_touch' });
    }
  };

  const openOrderDetailModal = (orderId, regels, totaal) => {
    if (!orderId || orderId.startsWith('regel-')) return;
    setOrderDetailModalData({ orderId, regels: regels || [], totaal: Number(totaal) || 0 });
    setOrderJourney(null);
    api.get(`/attributie/journey/${orderId}`)
      .then(res => setOrderJourney(res.data))
      .catch(() => setOrderJourney({ order_id: orderId, visitor_id: null, bezoeken: [], google_campagne: null, attributie_bron_methode: 'first_touch' }));
  };

  const openWooOrderById = (orderId) => {
    const id = orderId != null ? String(orderId).trim() : '';
    if (!id) return;
    api.get(`/woocommerce/sales/by-order/${encodeURIComponent(id)}`)
      .then((res) => {
        const regels = res.data?.regels || [];
        const totaal = regels.reduce((s, x) => s + (Number(parseFloat(x?.totaal_prijs)) || 0), 0);
        openOrderDetailModal(id, regels, totaal);
      })
      .catch(() => {
        alert('Orderregels konden niet worden geladen.');
      });
  };

  useEffect(() => {
    setExpandedAttributieBron(null);
    setCampaignBreakdownCache({});
    setShopOrdersForBronCache({});
    setMetaJourneyOrdersCache({});
  }, [dateStart, dateEnd]);

  const toggleAttributieCampaignRow = useCallback((bron) => {
    const platform = getCampaignBreakdownPlatform(bron);
    if (!platform) return;
    if (expandedAttributieBron === bron) {
      setExpandedAttributieBron(null);
      return;
    }
    setExpandedAttributieBron(bron);
    const cacheKey = `${platform}|${dateStart}|${dateEnd}`;
    const shopKey = `shop|${bron}|${dateStart}|${dateEnd}`;
    setCampaignBreakdownCache((prev) => ({
      ...prev,
      [cacheKey]: {
        loading: true,
        loaded: false,
        campaigns: prev[cacheKey]?.campaigns || [],
        error: null,
        metaPurchaseDeduped: false,
        metaAccountConversionsValue: null,
        metaCampaignSumConversionsValue: null
      }
    }));
    setShopOrdersForBronCache((prev) => ({
      ...prev,
      [shopKey]: { loading: true, orders: prev[shopKey]?.orders || [], error: null }
    }));
    api.get(`/attributie/ad-campaign-breakdown?start_date=${encodeURIComponent(dateStart)}&end_date=${encodeURIComponent(dateEnd)}&platform=${encodeURIComponent(platform)}`)
      .then((res) => {
        const d = res.data || {};
        setCampaignBreakdownCache((p) => ({
          ...p,
          [cacheKey]: {
            loading: false,
            loaded: true,
            campaigns: d.campaigns || [],
            error: null,
            metaPurchaseDeduped: !!d.meta_purchase_deduped,
            metaAccountConversionsValue: d.meta_account_conversions_value,
            metaCampaignSumConversionsValue: d.meta_campaign_sum_conversions_value
          }
        }));
      })
      .catch((e) => {
        setCampaignBreakdownCache((p) => ({
          ...p,
          [cacheKey]: {
            loading: false,
            loaded: true,
            campaigns: [],
            error: e.response?.data?.error || e.message,
            metaPurchaseDeduped: false,
            metaAccountConversionsValue: null,
            metaCampaignSumConversionsValue: null
          }
        }));
      });
    api.get(`/attributie/shop-orders-for-bron?start_date=${encodeURIComponent(dateStart)}&end_date=${encodeURIComponent(dateEnd)}&bron=${encodeURIComponent(bron)}`)
      .then((res) => {
        setShopOrdersForBronCache((p) => ({
          ...p,
          [shopKey]: { loading: false, orders: res.data?.orders || [], error: null }
        }));
      })
      .catch((e) => {
        setShopOrdersForBronCache((p) => ({
          ...p,
          [shopKey]: { loading: false, orders: [], error: e.response?.data?.error || e.message }
        }));
      });
    if (platform === 'meta') {
      const jKey = `metaJourney|${dateStart}|${dateEnd}`;
      setMetaJourneyOrdersCache((prev) => ({
        ...prev,
        [jKey]: { loading: true, loaded: false, orders: prev[jKey]?.orders || [], uitleg: prev[jKey]?.uitleg, error: null }
      }));
      api.get(`/attributie/shop-orders-meta-journey?start_date=${encodeURIComponent(dateStart)}&end_date=${encodeURIComponent(dateEnd)}`)
        .then((res) => {
          const d = res.data || {};
          setMetaJourneyOrdersCache((p) => ({
            ...p,
            [jKey]: {
              loading: false,
              loaded: true,
              orders: d.orders || [],
              uitleg: d.uitleg || null,
              error: null
            }
          }));
        })
        .catch((e) => {
          setMetaJourneyOrdersCache((p) => ({
            ...p,
            [jKey]: {
              loading: false,
              loaded: true,
              orders: [],
              uitleg: null,
              error: e.response?.data?.error || e.message
            }
          }));
        });
    }
  }, [expandedAttributieBron, dateStart, dateEnd]);

  const toggleExpandVisitor = (visitorId) => {
    if (expandedVisitorId === visitorId) {
      setExpandedVisitorId(null);
    } else {
      setExpandedVisitorId(visitorId);
    }
  };

  function groupBezoekenBySessions(bezoeken) {
    if (!Array.isArray(bezoeken) || bezoeken.length === 0) return [];
    const withDate = bezoeken.filter(b => b && (b.created_at != null));
    if (withDate.length === 0) return [];
    const sorted = [...withDate].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    const SESSION_GAP_MS = 30 * 60 * 1000;
    const sessions = [];
    const pathFor = (b) => {
      if (!b) return '—';
      if (b.url) { try { return new URL(b.url).pathname; } catch { return b.url; } }
      return b.landing_page || '—';
    };
    let current = [{ ...sorted[0], path: pathFor(sorted[0]) }];
    for (let i = 1; i < sorted.length; i++) {
      const prev = new Date(sorted[i - 1].created_at).getTime();
      const curr = new Date(sorted[i].created_at).getTime();
      const path = pathFor(sorted[i]);
      if (Number.isNaN(prev) || Number.isNaN(curr) || curr - prev > SESSION_GAP_MS) {
        sessions.push(current);
        current = [{ ...sorted[i], path }];
      } else {
        current.push({ ...sorted[i], path });
      }
    }
    sessions.push(current);
    return sessions;
  }

  /** Groepeer recente bezoeken per visitor_id; elke groep gesorteerd op nieuwste bezoek eerst. */
  function groupRecenteBezoekenByVisitor(bezoeken) {
    if (!Array.isArray(bezoeken) || bezoeken.length === 0) return [];
    const byVisitor = {};
    bezoeken.forEach(b => {
      if (!b || typeof b !== 'object') return;
      const vid = b.visitor_id != null ? String(b.visitor_id) : '__unknown__';
      if (!byVisitor[vid]) byVisitor[vid] = [];
      byVisitor[vid].push(b);
    });
    return Object.entries(byVisitor)
      .map(([vid, list]) => {
        const sorted = [...list].sort((a, b) => (new Date(b.created_at).getTime()) - (new Date(a.created_at).getTime()));
        return { visitor_id: vid === '__unknown__' ? null : vid, bezoeken: sorted };
      })
      .sort((a, b) => {
        const tA = a.bezoeken[0]?.created_at ? new Date(a.bezoeken[0].created_at).getTime() : 0;
        const tB = b.bezoeken[0]?.created_at ? new Date(b.bezoeken[0].created_at).getTime() : 0;
        return tB - tA;
      });
  }

  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  useEffect(() => {
    if (expandedVisitorId == null) return;
    const id = expandedVisitorId;
    fetchBezoekenForVisitor(id);
    const t = setInterval(() => fetchBezoekenForVisitor(id), 10000);
    return () => clearInterval(t);
  }, [expandedVisitorId, fetchBezoekenForVisitor]);

  useEffect(() => {
    const id = recenteVisitorModalId;
    if (id == null || id === '__unknown__') return;
    fetchBezoekenForVisitor(id);
    const t = setInterval(() => fetchBezoekenForVisitor(id), 10000);
    return () => clearInterval(t);
  }, [recenteVisitorModalId, fetchBezoekenForVisitor]);

  return (
    <div className="attributie-page attributie-dashboard">
      <div className="page-header dashboard-header">
        <h1 className="page-title"><FiActivity /> Attributie dashboard</h1>
        <p className="dashboard-subtitle">
          Bezoekers, bronnen en journey. Installatie van tracking staat onder <strong>Koppelingen → Attributie koppeling</strong>.
        </p>
        <div className="diagnose-row">
          <button
            type="button"
            className="btn btn-small btn-diagnose-standalone"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchDiagnose(); }}
            disabled={diagnoseLoading}
            aria-busy={diagnoseLoading}
          >
            {diagnoseLoading ? 'Diagnose laden…' : 'Diagnose tracking'}
          </button>
        </div>
        {(diagnoseLoading || (diagnose && typeof diagnose === 'object')) && (
          <div className="diagnose-result-block" role="status">
            {diagnoseLoading && <span className="diagnose-loading">Diagnose ophalen…</span>}
            {!diagnoseLoading && diagnose && typeof diagnose === 'object' && (
              <div className="diagnose-result">
                <span className="diagnose-line">
                  Pageviews <strong>laatste 5 min</strong>: <strong>{diagnose.bezoeken_laatste_5_min ?? '—'}</strong>
                  {diagnose.bezoeken_laatste_5_min_zonder_bots != null && (
                    <> (zonder bots: <strong>{diagnose.bezoeken_laatste_5_min_zonder_bots}</strong>)</>
                  )}
                  {' '}— dit zijn <em>losse hits</em>, niet per se unieke bezoekers.
                </span>
                {diagnose.venster_minuten != null && diagnose.unieke_live_bezoekers != null && (
                  <span className="diagnose-line diagnose-line-secondary">
                    {' '}Live-venster <strong>{diagnose.venster_minuten} min</strong>: <strong>{diagnose.unieke_live_bezoekers}</strong> uniek (zelfde als tegel Live)
                    {diagnose.pageviews_in_venster_zonder_bots != null && (
                      <> · pageviews z. bots in dat venster: <strong>{diagnose.pageviews_in_venster_zonder_bots}</strong></>
                    )}
                  </span>
                )}
                {diagnose.bezoeken_laatste_24_uur != null && <> · 24 u: <strong>{diagnose.bezoeken_laatste_24_uur}</strong></>}
                {diagnose.tracking_key_configured != null && <> · Tracking key: <strong>{diagnose.tracking_key_configured ? 'geconfigureerd' : 'niet gezet'}</strong></>}
                {diagnose.error && <> · Fout: {String(diagnose.error)}</>}
                {diagnose.uitleg && <span className="diagnose-hint" title={String(diagnose.uitleg)}> ⓘ</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rij 1: Overzicht bezoekers (50%) | Orders van de dag (50%) */}
      <div className="dashboard-row dashboard-top">
      <div className="dashboard-section dashboard-overview">
        <h2 className="section-title">Overzicht bezoekers</h2>
        <div className="date-filter date-filter-presets" style={{ marginBottom: '1rem' }}>
          <div className="preset-buttons">
            <span className="preset-label">Snel instellen:</span>
            <select
              className="preset-dropdown"
              value={selectedPreset}
              onChange={(e) => {
                const val = e.target.value;
                if (val) setDatePresetAttributie(val);
              }}
            >
              <option value="">—</option>
              <option value="yeartodate">Dit jaar tot heden</option>
              <option value="currentweek">Huidige week</option>
              <option value="currentmonth">Huidige maand</option>
              <option value="currentquarter">Huidig kwartaal</option>
              <option value="week">Afgelopen week</option>
              <option value="month">Afgelopen maand</option>
              <option value="quarter">Afgelopen kwartaal</option>
              <option value="halfyear">Afgelopen half jaar</option>
              <option value="year">Afgelopen jaar</option>
            </select>
          </div>
          <div className="date-inputs">
            <label>Van</label>
            <input type="date" value={dateStart} onChange={e => { setDateStart(e.target.value); setSelectedPreset(''); }} />
            <label>Tot</label>
            <input type="date" value={dateEnd} onChange={e => { setDateEnd(e.target.value); setSelectedPreset(''); }} />
            <span className="preset-label" style={{ marginLeft: '1rem' }}>Bron-attributie:</span>
            <select
              className="preset-dropdown"
              value={bronMethode}
              onChange={(e) => saveBronMethode(e.target.value)}
              title="Bij verrijking van orders met Direct/lege bron: first touch (eerste bezoek met bron) of last touch (laatste bezoek met bron), binnen 2 maanden"
            >
              <option value="first_touch">First touch</option>
              <option value="last_touch">Last touch</option>
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setLoading(true);
                fetchOverview();
                fetchBezoeken();
                fetchLive();
                fetchOrderwaardePeriode();
                fetchHerkomstPeriode();
                fetchVisitsByHour(dateEnd);
                fetchGemiddeldeTijdOnline();
                fetchAttributieTabel();
                fetchTrackingEvents();
              }}
            >
              <FiRefreshCw /> Vernieuwen
            </button>
          </div>
        </div>
        {loading ? (
          <div className="loading">Laden...</div>
        ) : overview && typeof overview === 'object' && (
          <div className="overview-cards">
            <div className="overview-card overview-card-1">
              <span className="card-value">{overview.totaal_bezoeken ?? 0}</span>
              <span className="card-label">Totaal bezoeken</span>
            </div>
            <div className="overview-card overview-card-2">
              <span className="card-value">{overview.unieke_bezoekers ?? 0}</span>
              <span className="card-label">Unieke bezoekers</span>
            </div>
            <div className="overview-card overview-card-3">
              <span className="card-value">
                € {(orderwaardePeriode != null ? orderwaardePeriode : 0).toFixed(2)}
              </span>
              <span className="card-label">Orderwaarde</span>
            </div>
          </div>
        )}
        <div className="herkomst-vandaag-attributie herkomst-met-blokken">
          <div className="herkomst-pie-col">
            <h3>Herkomst orders</h3>
            {Array.isArray(herkomstPeriode) && herkomstPeriode.length > 0 ? (
              <div className="herkomst-pie-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={herkomstPeriode}
                      dataKey="aantal_orders"
                      nameKey="bron_label"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ bron_label, aantal_orders } = {}) => {
                        const total = herkomstPeriode.reduce((s, d) => s + (Number(d && d.aantal_orders) || 0), 0);
                        const val = Number(aantal_orders) || 0;
                        const pct = total ? (val / total) * 100 : 0;
                        return pct >= 12 ? `${bron_label ?? ''}: ${val}` : null;
                      }}
                      labelLine
                    >
                      {herkomstPeriode.map((_, index) => (
                        <Cell key={index} fill={HERKOMST_COLORS[index % HERKOMST_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} orders`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="empty-state">Geen herkomstdata voor deze periode.</p>
            )}
          </div>
          <div className="herkomst-blokken-col">
            <div 
              className="herkomst-blok herkomst-blok-clickable herkomst-blok-live" 
              role="button" 
              tabIndex={0}
              onClick={() => document.getElementById('live-bezoekers')?.scrollIntoView({ behavior: 'smooth' })}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('live-bezoekers')?.scrollIntoView({ behavior: 'smooth' }); } }}
              aria-label="Ga naar live bezoekers"
            >
              <span className="herkomst-blok-label">Live bezoekers</span>
              <span className="herkomst-blok-waarde">{Array.isArray(liveVisitors) ? liveVisitors.length : 0}</span>
            </div>
            <div className="herkomst-blok">
              <span
                className="herkomst-blok-label"
                title="Met live bezoekers: gemiddelde tijd sinds eerste hit (zelfde logica als de live-lijst). Zonder live bezoekers: gemiddelde tijd tussen eerste en laatste pagina per bezoeker in de gekozen periode (één pagina telt als 0 s)."
              >
                Gemiddelde tijd online (in seconden of minuten)
              </span>
              <span className="herkomst-blok-waarde">{formatSeconden(herkomstGemiddeldeSeconden)}</span>
            </div>
          </div>
        </div>
        </div>
        <div className="dashboard-section orders-of-day-box">
          <h2 className="section-title">
            Verkopen {ordersOfDay?.start_date && ordersOfDay?.end_date
              ? (ordersOfDay.start_date === ordersOfDay.end_date ? ordersOfDay.start_date : `${ordersOfDay.start_date} t/m ${ordersOfDay.end_date}`)
              : dateStart === dateEnd ? dateStart : `${dateStart} t/m ${dateEnd}`}
          </h2>
          {((ordersOfDay && ordersOfDay.regels) || []).length === 0 ? (
            <p className="empty-state">Geen verkoopdata voor de gekozen periode.</p>
          ) : (
            <div className="orders-of-day-list">
              {(() => {
                const byOrder = {};
                const regels = Array.isArray(ordersOfDay?.regels) ? ordersOfDay.regels : [];
                regels.forEach(r => {
                  if (!r || typeof r !== 'object') return;
                  const id = r.woocommerce_order_id != null && r.woocommerce_order_id !== '' ? String(r.woocommerce_order_id) : `regel-${r.id}`;
                  if (!byOrder[id]) byOrder[id] = { regels: [], totaal: 0 };
                  byOrder[id].regels.push(r);
                  byOrder[id].totaal += Number(parseFloat(r.totaal_prijs)) || 0;
                });
                const showDate = ordersOfDay?.start_date && ordersOfDay?.end_date && ordersOfDay.start_date !== ordersOfDay.end_date;
                return Object.entries(byOrder).map(([orderId, { regels: orderRegels, totaal }]) => {
                  const isRegel = orderId.startsWith('regel-');
                  const totaalNum = Number(totaal) || 0;
                  const canOpenModal = !isRegel;
                  const orderDate = orderRegels[0]?.verkoopdatum && showDate
                    ? new Date(orderRegels[0].verkoopdatum + 'T12:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
                    : null;
                  return (
                    <div key={orderId} className="order-of-day-item">
                      <div
                        className={`order-of-day-header ${canOpenModal ? 'clickable' : ''}`}
                        onClick={() => canOpenModal && openOrderDetailModal(orderId, orderRegels, totaal)}
                        role={canOpenModal ? 'button' : undefined}
                        tabIndex={canOpenModal ? 0 : undefined}
                        onKeyDown={e => { if (canOpenModal && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openOrderDetailModal(orderId, orderRegels, totaal); } }}
                        aria-label={canOpenModal ? 'Klant en attributie openen' : undefined}
                      >
                        <strong>{isRegel ? 'Overige' : `Order #${orderId}`}{orderDate ? ` · ${orderDate}` : ''}</strong>
                        <span className="order-of-day-totaal">€ {totaalNum.toFixed(2)}</span>
                      </div>
                      <ul className="order-of-day-products">
                        {(Array.isArray(orderRegels) ? orderRegels : []).slice(0, 3).map((r, i) => (
                          <li key={i}>{r && r.product_naam != null ? r.product_naam : '—'} × {r && r.aantal != null ? r.aantal : '—'}</li>
                        ))}
                        {canOpenModal && <li className="more">Klik voor klant, bezoeken en herkomst</li>}
                      </ul>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
        {/* Popup: order met klant, bezoeken en herkomst */}
        <Modal
          show={!!orderDetailModalData}
          onClose={() => { setOrderDetailModalData(null); setOrderJourney(null); }}
          title={orderDetailModalData ? `Order #${orderDetailModalData.orderId}` : 'Order'}
          large
        >
          <div className="attributie-modal-body order-detail-modal">
            {orderDetailModalData && (
              <>
                <p className="order-detail-totaal"><strong>Totaal: € {orderDetailModalData.totaal.toFixed(2)}</strong></p>
                <h4>Producten</h4>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Aantal</th>
                      <th>Prijs</th>
                      <th>Totaal</th>
                      <th>Bron</th>
                      <th title="utm_campaign uit het eerste of laatste Google Ads-bezoek (instelling bron-attributie)">Google-campagne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orderDetailModalData.regels || []).map((r, i) => (
                      <tr key={i}>
                        <td>{r?.product_naam ?? '—'}</td>
                        <td>{r?.aantal ?? '—'}</td>
                        <td>€ {(Number(parseFloat(r?.eenheidsprijs)) || 0).toFixed(2)}</td>
                        <td>€ {(Number(parseFloat(r?.totaal_prijs)) || 0).toFixed(2)}</td>
                        <td>{r?.bron ?? '—'}</td>
                        <td>
                          {orderJourney === null ? '…' : (orderJourney.google_campagne || '—')}
                          {orderJourney && orderJourney.attributie_bron_methode && orderJourney.google_campagne && (
                            <span className="muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                              {orderJourney.attributie_bron_methode === 'last_touch' ? 'last touch' : 'first touch'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h4>Klant &amp; attributie</h4>
                {orderJourney === null ? (
                  <p className="loading-detail">Laden…</p>
                ) : orderJourney && (orderJourney.visitor_id || (orderJourney.bezoeken && orderJourney.bezoeken.length > 0)) ? (
                  <>
                    {orderJourney.visitor_id && <p><strong>Visitor ID:</strong> <code>{orderJourney.visitor_id}</code></p>}
                    <p><strong>Herkomst order:</strong> {orderDetailModalData.regels?.[0]?.bron ?? '—'}</p>
                    {orderJourney.bezoeken && orderJourney.bezoeken.length > 0 && (
                      <>
                        <p><strong>Bezoeken van deze klant ({orderJourney.bezoeken.length}):</strong></p>
                        <ul className="journey-bezoeken-list">
                          {orderJourney.bezoeken.map((b, i) => (
                            <li key={b.id || i}>
                              {formatDateTimeNL(b.created_at)} – {formatBronMedium(b.utm_source, b.utm_medium)}
                              {b.utm_campaign && ` – ${b.utm_campaign}`} – {b.landing_page || b.url || '—'}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {orderJourney.events && orderJourney.events.length > 0 && (
                      <>
                        <p><strong>Events ({orderJourney.events.length}):</strong></p>
                        <ul className="journey-bezoeken-list">
                          {orderJourney.events.map((e, i) => {
                            const evLink = e.display_link || e.link;
                            const evUtm = e.display_bron || e.source;
                            return (
                            <li key={e.id || i}>
                              {formatDateTimeNL(e.created_at)} – <strong>{e.event_name}</strong>
                              {e.product_name && ` – ${e.product_name}`}
                              {evUtm && ` · UTM: ${evUtm}`}
                              {evLink && (
                                <>
                                  {' · '}
                                  <a href={evLink} target="_blank" rel="noopener noreferrer">pagina</a>
                                </>
                              )}
                            </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <p>Geen koppeling met bezoeker bekend voor deze order.</p>
                )}
              </>
            )}
          </div>
        </Modal>
      </div>

      {/* Attributie-tabel: eigen (shop) | spend | platform Ads-sync | clicks/impr */}
      <div className="dashboard-section attributie-tabel-section">
        <div className="section-title-row">
          <h2 className="section-title">Attributie per bron</h2>
          <div className="section-actions">
            <button type="button" className="btn btn-small" onClick={() => { setAdMetricsForm({ bron: '', start_date: dateStart, end_date: dateEnd, spend: '', clicks: '', impressions: '', budget: '' }); setAdMetricsModalOpen(true); }}>
              Ad-uitgaven invoeren
            </button>
            <button type="button" className="btn btn-small" onClick={() => { setAdApiConfigModalOpen(true); fetchAdApiConfig(); fetchPinterestCapiConfig(); }}>
              API-koppeling
            </button>
            <button type="button" className="btn btn-small btn-primary" disabled={syncAdApiLoading} onClick={async () => { setSyncAdApiResult(null); setSyncAdApiLoading(true); try { const res = await api.post('/attributie/sync-ad-api'); setSyncAdApiResult(res.data); fetchAttributieTabel(); } catch (e) { setSyncAdApiResult({ error: e.response?.data?.error || e.message }); } finally { setSyncAdApiLoading(false); } }}>
              {syncAdApiLoading ? 'Sync…' : 'Sync nu'}
            </button>
          </div>
        </div>
        <details className="attributie-tabel-meta-details">
          <summary className="attributie-tabel-meta-summary">Uitleg kolommen &amp; API-status</summary>
          <div className="attributie-tabel-meta-body">
            <p className="hint">Overzicht per bron. <strong>Eigen meting</strong> = orders en omzet uit de shop (verkoopdata per bron), met ROAS/CPA t.o.v. spend. <strong>Cvr %</strong> (eigen) = shoporders ÷ bezoeken. <strong>Platform</strong> = conversies en conversiewaarde uit de Ads-API (na <strong>Sync nu</strong>); Meta telt daar alleen het pixel-event <code>purchase</code> (geen dubbele optelling met o.a. omni_purchase). <strong>Cvr %</strong> (platform) = platformconversies ÷ clicks. Bezoeken als <strong>bron / medium</strong>; <strong>▶</strong> voor campagnedetail.</p>
            <div className="api-koppeling-status">
              <span>API: Google Ads {adApiConfig.google?.configured ? <strong className="ok">gegevens opgeslagen</strong> : <span className="muted">niet gekoppeld</span>}</span>
              <span className="sep">|</span>
              <span>Meta {adApiConfig.meta?.configured ? <strong className="ok">gegevens opgeslagen</strong> : <span className="muted">niet gekoppeld</span>}</span>
              <span className="sep">|</span>
              <span>Pinterest Ads {adApiConfig.pinterest?.configured ? <strong className="ok">gegevens opgeslagen</strong> : <span className="muted">niet gekoppeld</span>}</span>
              <span className="sep">|</span>
              <span>Pinterest CAPI {pinterestCapiConfig?.configured ? <strong className="ok">actief</strong> : <span className="muted">niet gekoppeld</span>}</span>
              <span className="api-koppeling-hint">
                (Klik &quot;Sync nu&quot; voor <strong>advertentie-uitgaven</strong> uit Google/Meta/<strong>Pinterest Ads API</strong> — niet voor Pinterest CAPI. CAPI stuurt alleen conversies mee bij <strong>verkoopdata-sync</strong> naar Woo; die foutmelding bij Pinterest hieronder komt daarom meestal door een ontbrekende of verkeerde <strong>OAuth-token onder Pinterest Ads API</strong>, niet door CAPI.)
              </span>
              {syncAdApiResult && (syncAdApiResult.error ? (
                <span className="sync-result err">Sync: {syncAdApiResult.error}</span>
              ) : (
                <span className="sync-result">Sync: <span className={syncAdApiResult.google?.success ? 'ok' : 'err'}>Google {syncAdApiResult.google?.success ? `${syncAdApiResult.google.rows ?? 0} dagen` : (syncAdApiResult.google?.error || '—')}</span><span className="sep"> · </span><span className={syncAdApiResult.meta?.success ? 'ok' : 'err'}>Meta {syncAdApiResult.meta?.success ? `${syncAdApiResult.meta.rows ?? 0} dagen` : (syncAdApiResult.meta?.error || '—')}</span><span className="sep"> · </span><span className={syncAdApiResult.pinterest?.success ? 'ok' : 'err'}>Pinterest {syncAdApiResult.pinterest?.success ? `${syncAdApiResult.pinterest.rows ?? 0} dagen` : (syncAdApiResult.pinterest?.error || '—')}</span></span>
              ))}
            </div>
          </div>
        </details>
        {!attributieTabelData ? (
          <p className="empty-state">Laden…</p>
        ) : !attributieTabelData.rows || attributieTabelData.rows.length === 0 ? (
          <p className="empty-state">Geen data voor deze periode.</p>
        ) : (
          <div className="attributie-tabel-wrap">
            <table className="attributie-tabel attributie-tabel-dual-metric">
              <colgroup>
                <col className="col-bron" />
                <col className="col-num" />
                <col className="col-num attributie-col-eigen" />
                <col className="col-num attributie-col-eigen" />
                <col className="col-num attributie-col-eigen" />
                <col className="col-num attributie-col-eigen" />
                <col className="col-num attributie-col-eigen" />
                <col className="col-num" />
                <col className="col-num attributie-col-platform" />
                <col className="col-num attributie-col-platform" />
                <col className="col-num attributie-col-platform" />
                <col className="col-num attributie-col-platform" />
                <col className="col-num attributie-col-platform" />
                <col className="col-num" />
                <col className="col-num" />
                <col className="col-num" />
                <col className="col-num" />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-bron">Bron</th>
                  <th className="col-num">Bezoeken</th>
                  <th className="col-num attributie-col-eigen attributie-th-eigen" title="Orders toegeschreven aan deze bron (shop / verkoopdata)">
                    <span className="attributie-th-metric">
                      <FourShineMark size={17} decorative />
                      <span className="attributie-th-metric-label">Orders</span>
                    </span>
                  </th>
                  <th className="col-num attributie-col-eigen attributie-th-eigen" title="Omzet uit shop voor deze bron">
                    <span className="attributie-th-metric">
                      <FourShineMark size={17} decorative />
                      <span className="attributie-th-metric-label">Orderw.</span>
                    </span>
                  </th>
                  <th className="col-num attributie-col-eigen attributie-th-eigen" title="Orderw. (eigen) ÷ spend">
                    <span className="attributie-th-metric">
                      <FourShineMark size={17} decorative />
                      <span className="attributie-th-metric-label">ROAS</span>
                    </span>
                  </th>
                  <th className="col-num attributie-col-eigen attributie-th-eigen" title="Spend ÷ orders (eigen)">
                    <span className="attributie-th-metric">
                      <FourShineMark size={17} decorative />
                      <span className="attributie-th-metric-label">CPA</span>
                    </span>
                  </th>
                  <th className="col-num attributie-col-eigen attributie-th-eigen" title="Shoporders ÷ bezoeken (eigen meting)">
                    <span className="attributie-th-metric">
                      <FourShineMark size={17} decorative />
                      <span className="attributie-th-metric-label">Cvr %</span>
                    </span>
                  </th>
                  <th className="col-num" title="Zelfde spend voor eigen- en platformkolommen">Spend</th>
                  <th className="col-num attributie-col-platform" title="Conversies volgens Ads-platform (API-sync); kan fractioneel zijn">Conv.</th>
                  <th className="col-num attributie-col-platform" title="Conversiewaarde volgens platform">Orderw.</th>
                  <th className="col-num attributie-col-platform" title="Conversiewaarde platform ÷ spend">ROAS</th>
                  <th className="col-num attributie-col-platform" title="Spend ÷ conversies platform">CPA</th>
                  <th className="col-num attributie-col-platform" title="Platformconversies ÷ clicks">Cvr %</th>
                  <th className="col-num">Clicks</th>
                  <th className="col-num">Impr.</th>
                  <th className="col-num">CTR %</th>
                  <th className="col-num">CPM</th>
                </tr>
              </thead>
              <tbody>
                {attributieTabelData.rows.map((r, i) => {
                  const plat = getCampaignBreakdownPlatform(r.bron);
                  const cacheKey = plat ? `${plat}|${dateStart}|${dateEnd}` : '';
                  const shopKey = plat ? `shop|${r.bron}|${dateStart}|${dateEnd}` : '';
                  const cd = cacheKey ? campaignBreakdownCache[cacheKey] : null;
                  const shopOd = shopKey ? shopOrdersForBronCache[shopKey] : null;
                  const journeyKey = plat === 'meta' ? `metaJourney|${dateStart}|${dateEnd}` : '';
                  const journeyOd = journeyKey ? metaJourneyOrdersCache[journeyKey] : null;
                  const adsPlatLabel = plat ? getAdsPlatformLabel(plat) : '';
                  const camps = cd?.campaigns || [];
                  const sumCampSpend = camps.reduce((s, c) => s + (Number(c.spend) || 0), 0);
                  const parentSpend = Number(r.spend) || 0;
                  const denom = sumCampSpend > 0 ? sumCampSpend : (parentSpend > 0 ? parentSpend : 0);
                  return (
                    <React.Fragment key={r.bron || `row-${i}`}>
                      <tr className={i % 2 === 0 ? 'attributie-data-row-even' : 'attributie-data-row-odd'}>
                        <td className="col-bron attributie-bron-with-expand">
                          {plat ? (
                            <button
                              type="button"
                              className="attributie-campaign-expand-btn"
                              onClick={() => toggleAttributieCampaignRow(r.bron)}
                              aria-expanded={expandedAttributieBron === r.bron}
                              title={adsPlatLabel ? `Campagnes en shoporders (${adsPlatLabel}) tonen` : 'Tonen'}
                            >
                              {expandedAttributieBron === r.bron ? '▼' : '▶'}
                            </button>
                          ) : (
                            <span className="attributie-campaign-expand-placeholder" aria-hidden="true" />
                          )}
                          <span>{r.bron || '—'}</span>
                        </td>
                        <td className="col-num">{r.bezoeken}</td>
                        <td className="col-num attributie-col-eigen">{r.orders}</td>
                        <td className="col-num attributie-col-eigen">€ {(r.orderwaarde ?? 0).toFixed(2)}</td>
                        <td className="col-num attributie-col-eigen">{r.roas != null ? r.roas.toFixed(2) : '—'}</td>
                        <td className="col-num attributie-col-eigen">{r.cpa != null ? `€ ${r.cpa.toFixed(2)}` : '—'}</td>
                        <td className="col-num attributie-col-eigen">{r.eigen_cvr != null ? `${r.eigen_cvr}%` : '—'}</td>
                        <td className="col-num">€ {(r.spend ?? 0).toFixed(2)}</td>
                        <td className="col-num attributie-col-platform">
                          {r.platform_ads != null ? Number(r.platform_ads.conversions).toFixed(2) : '—'}
                        </td>
                        <td className="col-num attributie-col-platform">
                          {r.platform_ads != null ? `€ ${Number(r.platform_ads.conversions_value).toFixed(2)}` : '—'}
                        </td>
                        <td className="col-num attributie-col-platform">
                          {r.platform_ads?.roas != null ? r.platform_ads.roas.toFixed(2) : '—'}
                        </td>
                        <td className="col-num attributie-col-platform">
                          {r.platform_ads?.cpa != null ? `€ ${r.platform_ads.cpa.toFixed(2)}` : '—'}
                        </td>
                        <td className="col-num attributie-col-platform">
                          {r.platform_ads?.conversion_rate != null ? `${r.platform_ads.conversion_rate}%` : '—'}
                        </td>
                        <td className="col-num">{r.clicks ?? '—'}</td>
                        <td className="col-num">{r.impressions ?? '—'}</td>
                        <td className="col-num">{r.ctr != null ? `${r.ctr}%` : '—'}</td>
                        <td className="col-num">{r.cpm != null ? `€ ${r.cpm.toFixed(2)}` : '—'}</td>
                      </tr>
                      {expandedAttributieBron === r.bron && plat && (
                        <tr className="attributie-campaign-detail-row">
                          <td colSpan={17} className="attributie-campaign-detail-cell">
                            <>
                              {cd?.loading ? (
                                <p className="muted campaign-detail-status">Campagnes laden…</p>
                              ) : cd?.error ? (
                                <p className="err campaign-detail-status">{cd.error}</p>
                              ) : camps.length === 0 ? (
                                <p className="muted campaign-detail-status">
                                  Geen campagne-data voor deze periode. Voer <strong>Sync nu</strong> uit ({adsPlatLabel}-API) om campagnes op te slaan.
                                </p>
                              ) : (
                                <>
                                  <p className="muted campaign-detail-hint">
                                    {adsPlatLabel}-waarde waar beschikbaar. * = geschat uit shop (orderwaarde/orders verdeeld naar spend) · zonder * = uit {adsPlatLabel} (conversiedata).
                                  </p>
                                  {plat === 'meta' && cd?.metaPurchaseDeduped && cd?.metaAccountConversionsValue != null && (
                                    <p className="muted campaign-detail-hint attributie-meta-dedup-hint">
                                      <strong>Let op:</strong> Meta kan dezelfde aankoop bij meerdere campagnes rapporteren. Hieronder zijn Orderw./orders per campagne <strong> proportioneel aangepast</strong> zodat de som gelijk is aan het Meta <strong>account-totaal</strong> (€ {Number(cd.metaAccountConversionsValue).toFixed(2)}
                                      {cd.metaCampaignSumConversionsValue != null
                                        ? `; ruwe som campagnes was € ${Number(cd.metaCampaignSumConversionsValue).toFixed(2)}`
                                        : ''}
                                      ).
                                    </p>
                                  )}
                                  <div className="attributie-campaign-breakdown-wrap">
                                  <table className="attributie-campaign-breakdown-table">
                                    <thead>
                                      <tr>
                                        <th>Campagne</th>
                                        <th className="col-num">Spend</th>
                                        <th className="col-num">%</th>
                                        <th className="col-num">Orderw.</th>
                                        <th className="col-num">Orders</th>
                                        <th className="col-num">ROAS</th>
                                        <th className="col-num">CPA</th>
                                        <th className="col-num">Cvr %</th>
                                        <th className="col-num">Clicks</th>
                                        <th className="col-num">Impr.</th>
                                        <th className="col-num">CTR %</th>
                                        <th className="col-num">CPM</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {camps.map((c) => {
                                        const sp = Number(c.spend) || 0;
                                        const share = denom > 0 ? sp / denom : 0;
                                        const pct = Math.round(share * 1000) / 10;
                                        const allocOw = (Number(r.orderwaarde) || 0) * share;
                                        const allocOrd = (Number(r.orders) || 0) * share;
                                        const convVal = Number(c.conversions_value) || 0;
                                        const convs = Number(c.conversions) || 0;
                                        const roasEst = sp > 0 ? allocOw / sp : null;
                                        const cpaEst = allocOrd > 0 ? sp / allocOrd : null;
                                        const roasFromAds = sp > 0 && convVal > 0;
                                        const roasDisplay = roasFromAds ? convVal / sp : roasEst;
                                        const cpaFromAds = convs > 0;
                                        const cpaDisplay = cpaFromAds ? sp / convs : cpaEst;
                                        const cClicks = Number(c.clicks) || 0;
                                        const cvrPct = cClicks > 0 ? Math.round((convs / cClicks) * 10000) / 100 : null;
                                        const orderwFromAds = convVal > 0;
                                        const orderwDisplay = orderwFromAds ? convVal : allocOw;
                                        const ordersFromAds = convs > 0;
                                        const ordersDisplay = ordersFromAds
                                          ? convs
                                          : allocOrd >= 0.05
                                            ? allocOrd
                                            : null;
                                        return (
                                          <tr key={String(c.campaign_id)}>
                                            <td>{c.campaign_name || c.campaign_id}</td>
                                            <td className="col-num">€ {sp.toFixed(2)}</td>
                                            <td className="col-num">{pct}%</td>
                                            <td
                                              className={`col-num${orderwFromAds ? ' attributie-metric-from-ads' : ''}`}
                                              title={
                                                orderwFromAds
                                                  ? `Conversiewaarde uit ${adsPlatLabel}`
                                                  : 'Geschat: shop-orderwaarde × spend-aandeel'
                                              }
                                            >
                                              € {orderwDisplay.toFixed(2)}
                                              {!orderwFromAds ? <span className="attributie-est-star"> *</span> : null}
                                            </td>
                                            <td
                                              className={`col-num${ordersFromAds ? ' attributie-metric-from-ads' : ''}`}
                                              title={
                                                ordersFromAds
                                                  ? `Conversies uit ${adsPlatLabel}`
                                                  : 'Geschat: shop-orders × spend-aandeel'
                                              }
                                            >
                                              {ordersDisplay != null ? Number(ordersDisplay).toFixed(1) : '—'}
                                              {!ordersFromAds && ordersDisplay != null ? (
                                                <span className="attributie-est-star"> *</span>
                                              ) : null}
                                            </td>
                                            <td
                                              className={`col-num${roasFromAds ? ' attributie-metric-from-ads' : ''}`}
                                              title={
                                                roasFromAds
                                                  ? `ROAS = conversiewaarde ÷ spend (${adsPlatLabel})`
                                                  : 'Geschat: shop-orderwaarde-verdeling ÷ spend'
                                              }
                                            >
                                              {roasDisplay != null ? roasDisplay.toFixed(2) : '—'}
                                              {!roasFromAds && roasDisplay != null ? (
                                                <span className="attributie-est-star"> *</span>
                                              ) : null}
                                            </td>
                                            <td
                                              className={`col-num${cpaFromAds ? ' attributie-metric-from-ads' : ''}`}
                                              title={
                                                cpaFromAds
                                                  ? `CPA = spend ÷ conversies (${adsPlatLabel})`
                                                  : 'Geschat: spend ÷ geschatte orders'
                                              }
                                            >
                                              {cpaDisplay != null ? `€ ${cpaDisplay.toFixed(2)}` : '—'}
                                              {!cpaFromAds && cpaDisplay != null ? (
                                                <span className="attributie-est-star"> *</span>
                                              ) : null}
                                            </td>
                                            <td className="col-num">{cvrPct != null ? `${cvrPct}%` : '—'}</td>
                                            <td className="col-num">{c.clicks ?? '—'}</td>
                                            <td className="col-num">{c.impressions ?? '—'}</td>
                                            <td className="col-num">{c.ctr != null ? `${c.ctr}%` : '—'}</td>
                                            <td className="col-num">{c.cpm != null ? `€ ${c.cpm.toFixed(2)}` : '—'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                </>
                              )}
                              <div className="attributie-shop-orders-bron-wrap">
                                <h4 className="attributie-shop-orders-bron-title">Shoporders (eigen meting) — {r.bron || '—'}</h4>
                                <p className="muted campaign-detail-hint attributie-shop-orders-bron-hint">
                                  Zelfde bronlabel als in de attributietabel (verkoopdata). Klik een order voor producten en bezoektraject.
                                </p>
                                {shopOd?.loading ? (
                                  <p className="muted campaign-detail-status">Shoporders laden…</p>
                                ) : shopOd?.error ? (
                                  <p className="err campaign-detail-status">{shopOd.error}</p>
                                ) : (shopOd?.orders || []).length === 0 ? (
                                  <p className="muted campaign-detail-status">
                                    Geen shoporders met dit bronlabel in deze periode.
                                  </p>
                                ) : (
                                  <div className="attributie-campaign-breakdown-wrap">
                                    <table className="attributie-campaign-breakdown-table attributie-shop-orders-bron-table">
                                      <thead>
                                        <tr>
                                          <th>Order</th>
                                          <th className="col-num">Datum</th>
                                          <th className="col-num">Totaal</th>
                                          <th>Klant</th>
                                          <th />
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(shopOd.orders || []).map((o) => (
                                          <tr key={String(o.woocommerce_order_id)}>
                                            <td><code>{o.woocommerce_order_id}</code></td>
                                            <td className="col-num">{o.verkoopdatum || '—'}</td>
                                            <td className="col-num">€ {(Number(o.totaal_prijs) || 0).toFixed(2)}</td>
                                            <td>{(o.klant_naam && String(o.klant_naam).trim()) || (o.klant_email && String(o.klant_email).trim()) || '—'}</td>
                                            <td className="col-num">
                                              <button
                                                type="button"
                                                className="btn btn-small btn-secondary"
                                                onClick={() => openWooOrderById(o.woocommerce_order_id)}
                                              >
                                                Details
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                              {plat === 'meta' && (
                                <div className="attributie-shop-orders-bron-wrap attributie-meta-journey-wrap">
                                  <h4 className="attributie-shop-orders-bron-title">Orders nabij Meta/Facebook (klantreis)</h4>
                                  <p className="muted campaign-detail-hint attributie-shop-orders-bron-hint">
                                    {journeyOd?.uitleg
                                      || 'Meta Ads Insights bevat geen ordernummers. Hier: shoporders waar dezelfde bezoeker vóór de orderdatum een betaalde Meta/Facebook-visit had (UTM). Kandidaten voor wat pixel/CAPI kan hebben geregistreerd; Meta-attributie kan afwijken.'}
                                  </p>
                                  {journeyOd?.loading ? (
                                    <p className="muted campaign-detail-status">Laden…</p>
                                  ) : journeyOd?.error ? (
                                    <p className="err campaign-detail-status">{journeyOd.error}</p>
                                  ) : (journeyOd?.orders || []).length === 0 ? (
                                    <p className="muted campaign-detail-status">
                                      Geen orders met gekoppelde bezoeker + Meta/Facebook Ads-touch vóór orderdatum in deze periode (controleer order↔visitor-koppeling bij checkout).
                                    </p>
                                  ) : (
                                    <div className="attributie-campaign-breakdown-wrap">
                                      <table className="attributie-campaign-breakdown-table attributie-shop-orders-bron-table">
                                        <thead>
                                          <tr>
                                            <th>Order</th>
                                            <th className="col-num">Datum</th>
                                            <th className="col-num">Totaal</th>
                                            <th>Klant</th>
                                            <th />
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(journeyOd.orders || []).map((o) => (
                                            <tr key={`j-${String(o.woocommerce_order_id)}`}>
                                              <td><code>{o.woocommerce_order_id}</code></td>
                                              <td className="col-num">{o.verkoopdatum || '—'}</td>
                                              <td className="col-num">€ {(Number(o.totaal_prijs) || 0).toFixed(2)}</td>
                                              <td>{(o.klant_naam && String(o.klant_naam).trim()) || (o.klant_email && String(o.klant_email).trim()) || '—'}</td>
                                              <td className="col-num">
                                                <button
                                                  type="button"
                                                  className="btn btn-small btn-secondary"
                                                  onClick={() => openWooOrderById(o.woocommerce_order_id)}
                                                >
                                                  Details
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="summary-row">
                  <td className="col-bron"><strong>Totaal</strong></td>
                  <td className="col-num"><strong>{attributieTabelData.totals?.bezoeken ?? 0}</strong></td>
                  <td className="col-num attributie-col-eigen"><strong>{attributieTabelData.totals?.orders ?? 0}</strong></td>
                  <td className="col-num attributie-col-eigen"><strong>€ {(attributieTabelData.totals?.orderwaarde ?? 0).toFixed(2)}</strong></td>
                  <td className="col-num attributie-col-eigen"><strong>{attributieTabelData.totals?.roas != null ? attributieTabelData.totals.roas.toFixed(2) : '—'}</strong></td>
                  <td className="col-num attributie-col-eigen"><strong>{attributieTabelData.totals?.cpa != null ? `€ ${attributieTabelData.totals.cpa.toFixed(2)}` : '—'}</strong></td>
                  <td className="col-num attributie-col-eigen"><strong>{attributieTabelData.totals?.eigen_cvr != null ? `${attributieTabelData.totals.eigen_cvr}%` : '—'}</strong></td>
                  <td className="col-num"><strong>€ {(attributieTabelData.totals?.spend ?? 0).toFixed(2)}</strong></td>
                  <td className="col-num attributie-col-platform" title="Niet opgeteld over alle bronnen — zie per platformrij (google / cpc, meta / …)">
                    <strong>—</strong>
                  </td>
                  <td className="col-num attributie-col-platform" title="Zie per platformrij">
                    <strong>—</strong>
                  </td>
                  <td className="col-num attributie-col-platform" title="Zie per platformrij">
                    <strong>—</strong>
                  </td>
                  <td className="col-num attributie-col-platform" title="Zie per platformrij">
                    <strong>—</strong>
                  </td>
                  <td className="col-num attributie-col-platform" title="Zie per platformrij">
                    <strong>—</strong>
                  </td>
                  <td className="col-num"><strong>{attributieTabelData.totals?.clicks ?? '—'}</strong></td>
                  <td className="col-num"><strong>{attributieTabelData.totals?.impressions ?? '—'}</strong></td>
                  <td className="col-num"><strong>{attributieTabelData.totals?.ctr != null ? `${attributieTabelData.totals.ctr}%` : '—'}</strong></td>
                  <td className="col-num"><strong>{attributieTabelData.totals?.cpm != null ? `€ ${attributieTabelData.totals.cpm.toFixed(2)}` : '—'}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <Modal show={adMetricsModalOpen} onClose={() => setAdMetricsModalOpen(false)} title="Ad-uitgaven invoeren">
          <div className="attributie-modal-body">
            <p className="hint">Vul voor één bron de uitgaven in voor de gekozen periode. Getallen haal je uit Google Ads, Meta Ads of Pinterest (rapportage per bron/campagne).</p>
            <form onSubmit={(e) => { e.preventDefault(); api.post('/attributie/ad-metrics', { bron: adMetricsForm.bron, start_date: adMetricsForm.start_date, end_date: adMetricsForm.end_date, spend: adMetricsForm.spend, clicks: adMetricsForm.clicks, impressions: adMetricsForm.impressions, budget: adMetricsForm.budget || undefined }).then(() => { setAdMetricsModalOpen(false); fetchAttributieTabel(); }).catch(err => alert(err.response?.data?.error || 'Opslaan mislukt')); }}>
              <label>Bron (bijv. google, meta, pinterest)</label>
              <input type="text" value={adMetricsForm.bron} onChange={e => setAdMetricsForm(f => ({ ...f, bron: e.target.value }))} placeholder="google" required />
              <label>Van datum</label>
              <input type="date" value={adMetricsForm.start_date} onChange={e => setAdMetricsForm(f => ({ ...f, start_date: e.target.value }))} required />
              <label>Tot datum</label>
              <input type="date" value={adMetricsForm.end_date} onChange={e => setAdMetricsForm(f => ({ ...f, end_date: e.target.value }))} required />
              <label>Spend (€)</label>
              <input type="number" step="0.01" min="0" value={adMetricsForm.spend} onChange={e => setAdMetricsForm(f => ({ ...f, spend: e.target.value }))} placeholder="0" />
              <label>Clicks</label>
              <input type="number" min="0" value={adMetricsForm.clicks} onChange={e => setAdMetricsForm(f => ({ ...f, clicks: e.target.value }))} placeholder="0" />
              <label>Impressions</label>
              <input type="number" min="0" value={adMetricsForm.impressions} onChange={e => setAdMetricsForm(f => ({ ...f, impressions: e.target.value }))} placeholder="0" />
              <label>Budget (€, optioneel)</label>
              <input type="number" step="0.01" min="0" value={adMetricsForm.budget} onChange={e => setAdMetricsForm(f => ({ ...f, budget: e.target.value }))} placeholder="—" />
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setAdMetricsModalOpen(false)}>Annuleren</button>
                <button type="submit" className="btn btn-primary">Opslaan</button>
              </div>
            </form>
          </div>
        </Modal>
        <Modal show={adApiConfigModalOpen} onClose={() => setAdApiConfigModalOpen(false)} title="API-koppeling (Google, Meta, Pinterest)">
          <div className="attributie-modal-body api-config-modal">
            <p className="hint">Configureer inloggegevens voor automatische sync van ad-uitgaven (laatste 90 dagen). Gegevens worden lokaal opgeslagen. Na opslaan moet je &quot;Sync nu&quot; op de Attributie-pagina gebruiken om data op te halen. Pinterest CAPI stuurt conversies naar Pinterest bij elke order-sync. Krijg je 403 bij Google? Controleer: OAuth-scope <code>https://www.googleapis.com/auth/adwords</code>, geldige developer token (bij teststand alleen test-accounts), juiste Customer ID en bij MCC de Login customer ID.</p>
            <div className="api-config-block">
              <h3>Google Ads</h3>
              <label>Client ID</label>
              <input type="text" value={adApiConfigForm.google.client_id} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, client_id: e.target.value } }))} placeholder="xxx.apps.googleusercontent.com" />
              <label>Client secret</label>
              <input type="password" value={adApiConfigForm.google.client_secret} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, client_secret: e.target.value } }))} placeholder={adApiConfig.google?.configured ? '(opgeslagen)' : '—'} />
              <label>Developer token</label>
              <input type="text" value={adApiConfigForm.google.developer_token} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, developer_token: e.target.value } }))} placeholder={adApiConfig.google?.configured ? '(opgeslagen)' : '—'} />
              <label>Refresh token</label>
              <input type="password" value={adApiConfigForm.google.refresh_token} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, refresh_token: e.target.value } }))} placeholder={adApiConfig.google?.configured ? '(opgeslagen)' : '—'} />
              <label>Customer ID (zonder streepjes)</label>
              <input type="text" value={adApiConfigForm.google.customer_id} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, customer_id: e.target.value } }))} placeholder="1234567890" />
              <label>Login customer ID (alleen bij MCC/manager-account, optioneel)</label>
              <input type="text" value={adApiConfigForm.google.login_customer_id || ''} onChange={e => setAdApiConfigForm(f => ({ ...f, google: { ...f.google, login_customer_id: e.target.value } }))} placeholder="—" />
              {adApiConfig.google?.configured && <p className="hint api-config-saved-hint">Wachtwoordvelden (client secret, refresh token) zijn opgeslagen maar niet zichtbaar. Laat ze leeg om niet te wijzigen.</p>}
              <button type="button" className="btn btn-small btn-primary" onClick={() => api.post('/attributie/ad-api-config', { platform: 'google', config: adApiConfigForm.google }).then(() => { fetchAdApiConfig(); alert('Google Ads config opgeslagen.'); }).catch(err => alert(err.response?.data?.error || 'Opslaan mislukt'))}>Opslaan Google</button>
            </div>
            <div className="api-config-block">
              <h3>Meta (Facebook/Instagram Ads)</h3>
              <label>Access token</label>
              <input type="password" value={adApiConfigForm.meta.access_token} onChange={e => setAdApiConfigForm(f => ({ ...f, meta: { ...f.meta, access_token: e.target.value } }))} placeholder={adApiConfig.meta?.configured ? '(opgeslagen – alleen invullen om te wijzigen)' : '—'} />
              <label>Ad account ID (met of zonder act_)</label>
              <input type="text" value={adApiConfigForm.meta.ad_account_id} onChange={e => setAdApiConfigForm(f => ({ ...f, meta: { ...f.meta, ad_account_id: e.target.value } }))} placeholder="123456789012345" />
              {adApiConfig.meta?.configured && <p className="hint api-config-saved-hint">Access token is opgeslagen maar niet zichtbaar. Laat leeg om niet te wijzigen.</p>}
              <button type="button" className="btn btn-small btn-primary" onClick={() => api.post('/attributie/ad-api-config', { platform: 'meta', config: adApiConfigForm.meta }).then(() => { fetchAdApiConfig(); alert('Meta config opgeslagen.'); }).catch(err => alert(err.response?.data?.error || 'Opslaan mislukt'))}>Opslaan Meta</button>
            </div>
            <div className="api-config-block">
              <h3>Pinterest Ads API (spend, clicks, impressions)</h3>
              <p className="hint">Vult <strong>Attributie per bron</strong> (ROAS, CPA, …) via tabel <code>ad_bron_metrics</code>. Vereist een <strong>Pinterest Developer OAuth access token</strong> met ads-leesrechten (niet hetzelfde als het CAPI-conversietoken). Zonder OAuth-token probeert de sync het CAPI-token uit de sectie hieronder – dat werkt vaak niet voor spend. Zie ook <code>inkoop-software/ATTRIBUTIE_PINTEREST.md</code>.</p>
              <label>Access token (OAuth)</label>
              <input type="password" value={adApiConfigForm.pinterest.access_token} onChange={e => setAdApiConfigForm(f => ({ ...f, pinterest: { ...f.pinterest, access_token: e.target.value } }))} placeholder={adApiConfig.pinterest?.configured ? '(opgeslagen)' : 'OAuth access token uit Pinterest Developer App'} />
              <label>Ad account ID</label>
              <input type="text" value={adApiConfigForm.pinterest.ad_account_id} onChange={e => setAdApiConfigForm(f => ({ ...f, pinterest: { ...f.pinterest, ad_account_id: e.target.value } }))} placeholder="549767776682" />
              {adApiConfig.pinterest?.configured && <p className="hint api-config-saved-hint">Pinterest Ads API is geconfigureerd. Klik &quot;Sync nu&quot; om spend/clicks/impressions op te halen.</p>}
              <button type="button" className="btn btn-small btn-primary" onClick={() => api.post('/attributie/ad-api-config', { platform: 'pinterest', config: adApiConfigForm.pinterest }).then(() => { fetchAdApiConfig(); alert('Pinterest Ads config opgeslagen.'); }).catch(err => alert(err.response?.data?.error || 'Opslaan mislukt'))}>Opslaan Pinterest Ads</button>
            </div>
            <div className="api-config-block">
              <h3>Pinterest Conversions API (CAPI)</h3>
              <p className="hint">
                Stuurt conversies naar Pinterest bij elke <strong>verkoopdata-sync</strong> (Woo), niet bij &quot;Sync nu&quot; op deze pagina. Token: Ads Manager → Conversies-API → nieuw token. <strong>Ad account ID</strong> = het nummer van <em>jouw</em> Pinterest Ads-account (URL of instellingen), <strong>geen</strong> voorbeeld uit documentatie.
                Fouten bij events staan in de <strong>serverlog</strong> (<code>[WooCommerce] Pinterest CAPI</code>).
              </p>
              <label>Conversion access token</label>
              <input type="password" value={pinterestCapiForm.access_token} onChange={e => setPinterestCapiForm(f => ({ ...f, access_token: e.target.value }))} placeholder={pinterestCapiConfig?.configured ? '(opgeslagen – invullen om te wijzigen)' : 'Token uit Ads Manager'} />
              <label>Ad account ID</label>
              <input type="text" value={pinterestCapiForm.ad_account_id} onChange={e => setPinterestCapiForm(f => ({ ...f, ad_account_id: e.target.value }))} placeholder="Eigen ID uit Ads Manager (niet het documentatievoorbeeld)" />
              {pinterestCapiConfig?.configured && <p className="hint api-config-saved-hint">Pinterest CAPI is opgeslagen. Bij problemen: controleer token + account-ID; spend in Attributie vereist óók Pinterest Ads API hierboven.</p>}
              <button type="button" className="btn btn-small btn-primary" onClick={() => api.post('/attributie/pinterest-capi-config', { access_token: pinterestCapiForm.access_token, ad_account_id: pinterestCapiForm.ad_account_id }).then(() => { fetchPinterestCapiConfig(); alert('Pinterest CAPI config opgeslagen.'); }).catch(err => alert(err.response?.data?.error || 'Opslaan mislukt'))}>Opslaan Pinterest</button>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setAdApiConfigModalOpen(false)}>Sluiten</button>
            </div>
          </div>
        </Modal>
      </div>

      {/* Configurator-gebruik (50%) | Bezoekers per uur (50%) — bezoeken per bron weggelaten (dubbel met kaarten + herkomst) */}
      <div className="dashboard-row dashboard-row-per-bron">
        <div className="dashboard-section per-bron-half configurator-in-dashboard-row">
          <h2 className="section-title">Configurator-gebruik (Room Visualizer)</h2>
          <p className="hint configurator-dashboard-meta">
            <strong>{dateStart === dateEnd ? dateStart : `${dateStart} t/m ${dateEnd}`}</strong>
            {' · '}
            {trackingEventsSummary.room_visualizer_open > 0 ? (
              <><strong>{trackingEventsSummary.room_visualizer_open}</strong>× geopend in deze periode</>
            ) : (
              <>geen opens geregistreerd</>
            )}
          </p>
          {trackingEventsList.length > 0 ? (
            <div className="configurator-table-scroll configurator-table-scroll--in-dashboard">
              <div className="table-wrap">
                <table className="data-table configurator-events-table">
                  <thead>
                    <tr>
                      <th>Datum en tijd</th>
                      <th>Bezoeker</th>
                      <th>Event</th>
                      <th>Product</th>
                      <th>UTM / bron</th>
                      <th>Pagina (product)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingEventsList.map((e) => {
                      const rowLink = e.display_link || e.link;
                      const rowUtm = e.display_bron || e.source;
                      const page = configuratorPageLinkParts(rowLink);
                      return (
                        <tr key={e.id || `${e.visitor_id}-${e.created_at}-${e.event_name}`}>
                          <td>{formatDateTimeNL(e.created_at)}</td>
                          <td className="visitor-id" title={e.visitor_id}>{e.visitor_id ? `${String(e.visitor_id).slice(0, 14)}…` : '—'}</td>
                          <td>{e.event_name === 'room_visualizer_open' ? 'Configurator geopend' : e.event_name || '—'}</td>
                          <td>{e.product_name || '—'}</td>
                          <td>{rowUtm || '—'}</td>
                          <td className="url-cell">
                            {page.href ? (
                              <a href={page.href} target="_blank" rel="noopener noreferrer" title={page.title}>{page.text}</a>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="empty-state">Geen configurator-gebruik in de geselecteerde periode.</p>
          )}
        </div>
        <div className="dashboard-section visits-per-hour-chart">
          <h2 className="section-title" title="Uren in Europe/Amsterdam (niet UTC). Sluit aan op de datum die je hierboven kiest.">
            Bezoekers per uur {dateEnd ? `(${dateEnd})` : ''} <span className="chart-tz-hint">· NL-tijd</span>
          </h2>
          {Array.isArray(visitsPerHour) && visitsPerHour.length > 0 ? (
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={visitsPerHour} margin={{ top: 6, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="hour" tickFormatter={h => `${h}:00`} />
                  <YAxis allowDecimals={false} width={32} />
                  <Tooltip formatter={(value) => [value, 'Bezoeken']} labelFormatter={label => `${label}:00`} />
                  <Line type="monotone" dataKey="count" name="Bezoeken" stroke="#0d9488" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="empty-state">Geen uurlijkse data voor deze dag.</p>
          )}
        </div>
      </div>

      {/* Live bezoekers */}
      <div id="live-bezoekers" className="dashboard-section live-bezoekers">
        <div className="section-title-row">
          <h2 className="section-title">
            <FiRadio /> Live bezoekers
            {Array.isArray(liveVisitors) && liveVisitors.length > 0 && (
              <span className="live-count"> ({liveVisitors.length} actief)</span>
            )}
          </h2>
          <button type="button" className="btn btn-small" onClick={fetchLive} title="Live nu vernieuwen">
            <FiRefreshCw /> Vernieuw
          </button>
        </div>
        <div className="date-filter" style={{ marginBottom: '0.75rem' }}>
          <label>Periode:</label>
          <select value={liveMinutes} onChange={e => setLiveMinutes(Number(e.target.value))}>
            <option value={1}>1 minuut</option>
            <option value={5}>5 minuten</option>
            <option value={10}>10 minuten</option>
            <option value={15}>15 minuten</option>
          </select>
        </div>
        {!Array.isArray(liveVisitors) || liveVisitors.length === 0 ? (
          <p className="empty-state">Geen actieve bezoekers in de gekozen periode.</p>
        ) : isMobile ? (
          <div className="live-visitors-mobile">
            {(Array.isArray(liveVisitors) ? liveVisitors : []).map((v, idx) => {
              const vid = v && v.visitor_id;
              const displayName = (vid && visitorCustomers[vid]) ? visitorCustomers[vid] : (vid ? String(vid) : '—');
              const countryCode = (v && v.country && String(v.country).length === 2) ? String(v.country).toUpperCase() : null;
              const flag = countryCode ? countryToFlag(countryCode) : null;
              const currentPage = v?.last_url ? (() => { try { const p = new URL(v.last_url).pathname; return p || '/'; } catch { return String(v.last_url); } })() : '—';
              const funnelCls = liveVisitorFunnelClass(v);
              return (
                <div
                  key={vid != null ? vid : `row-${idx}`}
                  className={`live-visitor-card${funnelCls ? ` ${funnelCls}` : ''}`}
                  onClick={() => vid != null && toggleExpandVisitor(vid)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && vid != null) { e.preventDefault(); toggleExpandVisitor(vid); } }}
                  aria-label="Bezoekerdetails bekijken"
                >
                  <div className="live-visitor-card-header">
                    <div className="live-visitor-card-id">
                      <span className="live-visitor-id-text" title={vid}>{displayName}</span>
                    </div>
                    <span className="live-visitor-ago">{formatSecondsAgo(v?.last_seen)}</span>
                  </div>
                  <div className="live-visitor-card-page">
                    <span className="live-visitor-page-label">Pagina</span>
                    <span className="live-visitor-page-url">{currentPage}</span>
                  </div>
                  <div className="live-visitor-card-duration">
                    <div className="live-visitor-duration-left">
                      <span className="live-visitor-duration-label">Tijd</span>
                      <span className="live-visitor-duration-value">{formatDurationHMS(v?.duration_sec)}</span>
                    </div>
                    <div className="live-visitor-duration-meta">
                      {flag && <span className="live-visitor-flag live-visitor-flag--footer" title={countryCode}>{flag}</span>}
                      <span
                        className={`live-shop-badge live-shop-badge--mobile-foot live-shop-badge--${liveShopPlatform(v)}`}
                        title={liveShopPlatform(v) === 'shopify' ? 'Shopify' : 'WooCommerce'}
                      >
                        {liveShopPlatform(v) === 'shopify' ? 'Shopify' : 'Woo'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table live-visitors-table">
              <thead>
                <tr>
                  <th>Bezoeker</th>
                  <th>Shop</th>
                  <th>Laatst gezien</th>
                  <th>Huidige pagina</th>
                  <th>Referrer</th>
                  <th>Locatie</th>
                  <th>Bron</th>
                  <th>Device</th>
                  <th>Configurator</th>
                  <th>Tijd in periode</th>
                  <th>Pagina's</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(liveVisitors) ? liveVisitors : []).map((v, idx) => {
                  const vid = v && v.visitor_id;
                  const expanded = expandedVisitorId === vid;
                  const bezoeken = vid != null ? visitorBezoekenCache[vid] : undefined;
                  const sessions = bezoeken ? groupBezoekenBySessions(bezoeken) : [];
                  const displayName = (vid && visitorCustomers[vid]) ? visitorCustomers[vid] : (vid ? `${String(vid).slice(0, 14)}…` : '—');
                  const countryCode = (v && v.country && String(v.country).length === 2) ? String(v.country).toUpperCase() : null;
                  const flag = countryCode ? countryToFlag(countryCode) : null;
                  const currentPage = v?.last_url ? (() => { try { const p = new URL(v.last_url).pathname; return p || '/'; } catch { return String(v.last_url); } })() : '—';
                  const funnelCls = liveVisitorFunnelClass(v);
                  return (
                    <React.Fragment key={vid != null ? vid : `row-${idx}`}>
                      <tr
                        className={`clickable-row ${expanded ? 'expanded' : ''}${funnelCls ? ` ${funnelCls}` : ''}`}
                        onClick={() => vid != null && toggleExpandVisitor(vid)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && vid != null) { e.preventDefault(); toggleExpandVisitor(vid); } }}
                        aria-expanded={expanded}
                        aria-label={expanded ? 'Details inklappen' : 'Details uitklappen'}
                      >
                        <td className="visitor-id" data-label="Bezoeker">
                          <span className="visitor-name">{displayName}</span>
                          {flag && <span className="visitor-flag" title={countryCode}>{flag}</span>}
                        </td>
                        <td className="live-shop-cell" data-label="Shop">
                          <span className={`live-shop-badge live-shop-badge--${liveShopPlatform(v)}`}>
                            {liveShopPlatform(v) === 'shopify' ? 'Shopify' : 'Woo'}
                          </span>
                        </td>
                        <td data-label="Laatst gezien">{formatSecondsAgo(v?.last_seen)}</td>
                        <td className="url-cell" data-label="Huidige pagina" title={v?.last_url || ''}>
                          <span className="url-cell-label">Pagina:</span>
                          <span className="url-cell-value">{currentPage}</span>
                        </td>
                        <td className="referrer-cell" data-label="Referrer" title={v?.referrer || ''}>{formatReferrerShort(v?.referrer)}</td>
                        <td className="locatie-cell" data-label="Locatie">
                          {formatLocatie(v || {})}
                          {isMogelijkBot(v?.user_agent) && <span className="bot-badge" title="User-agent lijkt op bot/crawler">Mogelijk bot</span>}
                        </td>
                        <td data-label="Bron">{formatBronMedium(v?.utm_source, v?.utm_medium)}</td>
                        <td data-label="Device">{v?.device_type || '—'}</td>
                        <td data-label="Configurator" title={v?.configurator_events?.length ? v.configurator_events.map(e => `Configurator${e.product_name ? ': ' + e.product_name : ''} (${formatSecondsAgo(e.created_at)})`).join('\n') : 'Heeft de Room Visualizer op een productpagina geopend (niet alleen categorie-icoon)'}>
                          {v?.used_configurator ? (
                            <span className="configurator-cell">
                              <span className="configurator-badge">Ja</span>
                              {v.configurator_events?.length > 0 && (
                                <span className="configurator-detail">
                                  {v.configurator_events[0].product_name || v.configurator_events[0].event_name} · {formatSecondsAgo(v.configurator_events[0].created_at)}
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="duration-cell" data-label="Tijd in periode">
                          <span className="duration-cell-label duration-cell-label-long">Tijd op site:</span>
                          <span className="duration-cell-label duration-cell-label-short">Tijd:</span>
                          <span className="duration-cell-value">{formatDurationHMS(v?.duration_sec)}</span>
                        </td>
                        <td data-label="Pagina's">{v?.page_views ?? '—'}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Popup met bezoekerdetails (o.a. op mobiel) */}
        <Modal
          show={!!expandedVisitorId}
          onClose={() => setExpandedVisitorId(null)}
          title="Bezoeker – bezochte pagina's"
          large={false}
        >
          <div className="attributie-modal-body visitor-detail">
            {expandedVisitorId && (() => {
              const bezoeken = visitorBezoekenCache[expandedVisitorId];
              const sessions = bezoeken ? groupBezoekenBySessions(bezoeken) : [];
              const displayName = (visitorCustomers[expandedVisitorId]) ? visitorCustomers[expandedVisitorId] : `${String(expandedVisitorId).slice(0, 14)}…`;
              const liveRow = (Array.isArray(liveVisitors) ? liveVisitors : []).find((x) => x && x.visitor_id === expandedVisitorId);
              const configuratorEvents = liveRow?.configurator_events;
              const liveCart = liveRow?.cart;
              const marketingSignup =
                liveRow?.marketing_signup || visitorMarketing[expandedVisitorId] || null;
              return (
                <>
                  <p className="visitor-modal-name"><strong>{displayName}</strong></p>
                  <MarketingSignupDetail signup={marketingSignup} />
                  <p className="visitor-test-reset-row">
                    <button
                      type="button"
                      className="btn btn-small btn-secondary"
                      onClick={() => resetVisitorForTest(expandedVisitorId)}
                    >
                      Pop-up test reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-small btn-secondary"
                      onClick={() =>
                        unlinkVisitorEmail(
                          expandedVisitorId,
                          marketingSignup?.email || visitorCustomers[expandedVisitorId]
                        )
                      }
                    >
                      Ontkoppel browser van e-mail
                    </button>
                    <span className="muted hint">
                      Reset = inschrijving/cooldown. Ontkoppel = verkeerde klant-e-mail losmaken (orders blijven).
                    </span>
                  </p>
                  {configuratorEvents && configuratorEvents.length > 0 && (
                    <>
                      <h4>Configurator (Room Visualizer)</h4>
                      <ul className="configurator-events-list">
                        {configuratorEvents.map((e, i) => (
                          <li key={i}>
                            <span className="event-name">{e.event_name === 'room_visualizer_open' ? 'Configurator geopend' : e.event_name}</span>
                            {e.product_name && <span className="event-product"> · {e.product_name}</span>}
                            <span className="event-time"> · {formatSecondsAgo(e.created_at)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {liveCart != null && (
                    <>
                      <h4>Winkelwagen (laatste snapshot)</h4>
                      {Array.isArray(liveCart.lines) && liveCart.lines.length > 0 ? (
                        <ul className="configurator-events-list">
                          {liveCart.lines.map((l, i) => (
                            <li key={i}>
                              <span className="event-name">{(l.name || 'Product').trim()}</span>
                              <span className="event-product"> ×{Number(l.quantity) || 0}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="hint">Geen regels (leeg of alleen totaal gemeld).</p>
                      )}
                    </>
                  )}
                  {!bezoeken ? (
                    <p className="loading-detail">Bezoeken laden…</p>
                  ) : sessions.length === 0 ? (
                    <p>Geen eerdere bezoeken gevonden.</p>
                  ) : (
                    <>
                      <h4>Bezochte pagina's en sessies</h4>
                      {sessions.map((session, idx) => (
                        <div key={idx} className="session-block">
                          <div className="session-header">
                            Sessie {idx + 1} – {session.length} pagina{session.length !== 1 ? "'s" : ''} – {formatDateTimeNL(session[0]?.created_at)}
                          </div>
                          <ul className="session-pages">
                            {session.map((b, i) => (
                              <li key={i}>
                                <span className="page-time">{formatTimeNL(b.created_at)}</span>
                                <span className="page-path">{b.path || '—'}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </Modal>
      </div>

      {/* Laatste bezoeken – gegroepeerd per bezoeker, open/dicht klapbaar */}
      <div className="dashboard-section recente-bezoeken-section">
        <div className="section-title-row">
          <h2 className="section-title">Recente bezoeken</h2>
          <button type="button" className="btn btn-small" onClick={fetchBezoeken} title="Recente bezoeken nu vernieuwen">
            <FiRefreshCw /> Vernieuw
          </button>
        </div>
        <details className="collapsible-uitleg">
          <summary className="collapsible-uitleg-summary">Uitleg recente bezoeken</summary>
          <div className="collapsible-uitleg-body">
            <p className="hint recente-bezoeken-hint">Klik op een bezoeker om alle bezoeken en details in een popup te zien. Vernieuwt automatisch elke 30 seconden.</p>
          </div>
        </details>
        <div className="recente-bezoeken-scrollbox">
        {(Array.isArray(bezoeken) ? bezoeken : []).length === 0 ? (
          <p className="empty-state">Nog geen bezoeken. Zet het tracking-script op je webshop (zie Koppelingen → Attributie koppeling).</p>
        ) : (
          <div className="table-wrap recente-bezoeken-grouped">
            <table className="data-table recente-bezoeken-table">
              <thead>
                <tr>
                  <th>Bezoeker</th>
                  <th>Bezoeken</th>
                  <th className="recente-col-shop">Webshop</th>
                  <th>Laatste activiteit</th>
                </tr>
              </thead>
              <tbody>
                {groupRecenteBezoekenByVisitor(bezoeken).map(({ visitor_id: groupVid, bezoeken: groupBezoeken }) => {
                  const firstBezoek = groupBezoeken[0];
                  const lastDate = firstBezoek?.created_at ? (() => {
                    const d = parseServerDate(firstBezoek.created_at);
                    return formatDateTimeNL(parseServerDate(firstBezoek.created_at)) || firstBezoek.created_at || '—';
                  })() : '—';
                  const displayName = (groupVid && visitorCustomers[groupVid]) ? visitorCustomers[groupVid] : (groupVid ? `${String(groupVid).slice(0, 12)}…` : 'Onbekend');
                  return (
                    <tr
                      key={groupVid ?? '__unknown__'}
                      className="recente-group-row clickable-row"
                      onClick={() => {
                        setRecenteVisitorModalId(groupVid ?? '__unknown__');
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setRecenteVisitorModalId(groupVid ?? '__unknown__');
                        }
                      }}
                      aria-label="Alle bezoeken en details openen"
                    >
                      <td className="visitor-id">{displayName}</td>
                      <td>{groupBezoeken.length} bezoek{groupBezoeken.length !== 1 ? 'en' : ''}</td>
                      <td className="recente-col-shop">
                        <span
                          className={`live-shop-badge live-shop-badge--${liveShopPlatform(firstBezoek)}`}
                          title={liveShopPlatform(firstBezoek) === 'shopify' ? 'Shopify' : 'WooCommerce'}
                        >
                          {liveShopPlatform(firstBezoek) === 'shopify' ? 'Shopify' : 'Woo'}
                        </span>
                      </td>
                      <td>{lastDate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>
        {/* Popup: alle bezoeken van deze bezoeker met volledige info */}
        <Modal
          show={!!recenteVisitorModalId}
          onClose={() => setRecenteVisitorModalId(null)}
          title={recenteVisitorModalId ? `Bezoeken van ${(visitorCustomers[recenteVisitorModalId]) || recenteVisitorModalId.slice(0, 14) + '…'}` : 'Bezoeken'}
          large
        >
          <div className="attributie-modal-body visitor-detail">
            {recenteVisitorModalId && (() => {
              const vid = recenteVisitorModalId === '__unknown__' ? null : recenteVisitorModalId;
              const list = vid ? visitorBezoekenCache[vid] : null;
              const sessions = list ? groupBezoekenBySessions(list) : [];
              const displayName = (vid && visitorCustomers[vid]) ? visitorCustomers[vid] : (vid ? `${String(vid).slice(0, 14)}…` : 'Onbekend');
              if (vid && list === undefined) return <p className="loading-detail">Bezoeken laden…</p>;
              if (!list || list.length === 0) return <p>Geen bezoeken gevonden voor deze bezoeker.</p>;
              const marketingSignup = vid ? visitorMarketing[vid] || null : null;
              return (
                <>
                  <p className="visitor-modal-name"><strong>{displayName}</strong> – {list.length} bezoek{list.length !== 1 ? 'en' : ''}</p>
                  <MarketingSignupDetail signup={marketingSignup} />
                  {vid ? (
                    <p className="visitor-test-reset-row">
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        onClick={() => resetVisitorForTest(vid)}
                      >
                        Pop-up test reset
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        onClick={() =>
                          unlinkVisitorEmail(vid, marketingSignup?.email || visitorCustomers[vid])
                        }
                      >
                        Ontkoppel browser van e-mail
                      </button>
                      <span className="muted hint">
                        Reset = inschrijving/cooldown. Ontkoppel = verkeerde klant-e-mail losmaken.
                      </span>
                    </p>
                  ) : null}
                  {list.map((b, idx) => {
                    const dateStr = formatDateTimeNL(b.created_at);
                    return (
                      <div key={b.id || idx} className="bezoek-detail-block">
                        <h4>Bezoek {idx + 1} – {dateStr}</h4>
                        <dl className="bezoek-data-list">
                          <dt>URL</dt><dd className="url-dd">{b.url || '—'}</dd>
                          <dt>Landing</dt><dd>{b.landing_page || '—'}</dd>
                          <dt>Referrer</dt><dd className="url-dd">{b.referrer || '—'}</dd>
                          <dt>Bron</dt><dd>{formatBronMedium(b.utm_source, b.utm_medium)}</dd>
                          <dt>Campagne</dt><dd>{b.utm_campaign || '—'}</dd>
                          <dt>Device</dt><dd>{b.device_type || '—'}</dd>
                          <dt>Webshop</dt><dd>{liveShopPlatform(b) === 'shopify' ? 'Shopify' : 'WooCommerce'}</dd>
                          <dt>Locatie</dt><dd>{formatLocatie(b)}</dd>
                        </dl>
                      </div>
                    );
                  })}
                  <h4>Alle pagina's van deze bezoeker</h4>
                  {sessions.length === 0 ? (
                    <p>Geen sessies.</p>
                  ) : (
                    sessions.map((session, idx) => (
                      <div key={idx} className="session-block">
                        <div className="session-header">
                          Sessie {idx + 1} – {session.length} pagina{session.length !== 1 ? "'s" : ''} – {formatDateTimeNL(session[0]?.created_at)}
                        </div>
                        <ul className="session-pages">
                          {session.map((s, i) => (
                            <li key={i}>
                              <span className="page-time">{formatTimeNL(s.created_at)}</span>
                              <span className="page-path">{s.path || '—'}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </>
              );
            })()}
          </div>
        </Modal>
      </div>

      {/* Journey per order */}
      <div className="dashboard-section">
        <h2 className="section-title"><FiEye /> Journey per order</h2>
        <p>Vul een WooCommerce order-ID in om alle bezoeken van die klant te zien (als de order aan een visitor is gekoppeld).</p>
        <div className="journey-lookup">
          <input
            type="text"
            placeholder="Order-ID (bijv. 10661)"
            value={journeyOrderId}
            onChange={e => setJourneyOrderId(e.target.value)}
          />
          <button type="button" className="btn btn-primary" onClick={fetchJourney}>Bekijk journey</button>
        </div>
        {journey && (
          <div className="journey-result">
            <p><strong>Order {journey.order_id}</strong> {journey.visitor_id ? ` – visitor: ${journey.visitor_id}` : ' – geen visitor gekoppeld'}</p>
            {journey.bezoeken?.length === 0 && (!journey.events || journey.events.length === 0) ? (
              <p>Geen bezoeken of events gevonden voor deze order.</p>
            ) : (
              <>
                {journey.bezoeken && journey.bezoeken.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Datum/tijd</th>
                        <th>Bron</th>
                        <th>Landing / URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journey.bezoeken.map((b, i) => (
                        <tr key={b.id}>
                          <td>{i + 1}</td>
                          <td>{formatDateTimeNL(b.created_at)}</td>
                          <td>{formatBronMedium(b.utm_source || '(direct)', b.utm_medium)}</td>
                          <td>{b.landing_page || b.url || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {journey.events && journey.events.length > 0 && (
                  <>
                    <p><strong>Events:</strong></p>
                    <ul className="journey-bezoeken-list">
                      {journey.events.map((e, i) => {
                        const evLink = e.display_link || e.link;
                        const evUtm = e.display_bron || e.source;
                        return (
                        <li key={e.id || i}>
                          {formatDateTimeNL(e.created_at)} – <strong>{e.event_name}</strong>
                          {e.product_name && ` – ${e.product_name}`}
                          {evUtm && ` · UTM: ${evUtm}`}
                          {evLink && (
                            <>
                              {' · '}
                              <a href={evLink} target="_blank" rel="noopener noreferrer">pagina</a>
                            </>
                          )}
                        </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
